import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Add,
  ArticleOutlined as ArticleOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  DeleteSweep as DeleteSweepIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore,
  FileDownload as FileDownloadIcon,
  FolderOpen as FolderOpenIcon,
  MergeType as MergeTypeIcon,
  PersonOutline,
  PlayArrow as PlayArrowIcon,
  ReadMore as ReadMoreIcon,
  Replay as ReplayIcon,
  Stop as StopIcon,
  Visibility,
  VisibilityOff,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
} from "@mui/icons-material";
import {
  BotRegistryItem,
  MajorJob,
  useCreateBackendUserBotConnectionMutation,
  useCreateMajorJobMutation,
  useCreateTasksBatchMutation,
  useDeleteConnectionMutation,
  useDeleteMajorJobMutation,
  useDeleteTaskJobMutation,
  useDeleteTaskMutation,
  useGetBotRegistryQuery,
  useGetMajorJobsQuery,
  useGetTaskJobsQuery,
  useTaskJobsBatchDeleteMutation,
  useAddTasksToJobMutation,
  useTasksBatchDeleteMutation,
  useRunTaskMutation,
  useRunTasksBatchMutation,
  useStopTaskMutation,
  useStopTasksBatchMutation,
  useSetTaskPendingMutation,
  useSubmitTask2FACodeMutation,
  useUpdateConnectionMutation,
  useUpdateAdminTaskMutation,
  useUpdateMajorJobMutation,
  useUpdateTaskJobMutation,
  useGetBackendUserStorageFoldersQuery,
  useLazyGetJobMatchesFileContentQuery,
  useDeleteJobMatchesFileMutation,
  usePresignJobMatchesMergeUploadMutation,
  useMergeJobMatchesReportsMutation,
  useLazyGetJobLogsArchiveQuery,
  useClearJobLogsMutation,
  useCreateMajorJobSsnJobMutation,
  useDeleteMajorJobSsnJobMutation,
  useDeleteMajorJobSsnFileMutation,
  useLazyGetMajorJobSsnFileContentQuery,
  adminUsersApi,
} from "features/adminUsers/adminUsers";
import { useAppDispatch, useAppSelector } from "features/store";
import { botsManagementWs, BotsManagementWsEvent } from "features/ws/botsManagementWs";
import { ChangeEvent, Dispatch, Fragment, ReactElement, ReactNode, SetStateAction, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AdminVendorConnection } from "types/interfaces/adminUsers";
import BotsTableScroll, { BOTS_HEADER_CELL_SX, BOTS_BODY_CELL_SX } from "components/botsManagement/BotsTableScroll";
import DeleteJobConfirmDialog from "components/botsManagement/DeleteJobConfirmDialog";
import { buildDataStorageFolderMappingSeed } from "utils/dataStorageNavigation";
import { extractSkipErrorEmployees, applySkipErrorEmployees } from "utils/additionalOptions";
import { sanitizeEmployeesInput, parseEmployeesNumber } from "utils/employeesNumber";

type ConnPlanEditorRow = {
  connection_id: number | "";
  /** Raw digit string while editing (may be ""); parsed in normalizePlans / bulk-add helpers. */
  task_target_count: string;
};

function connPlanTasksDigitsOnly(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 4);
}

/** API-ready connection plan row (numeric connection + positive task count). */
type NormalizedConnPlanRow = {
  connection_id: number;
  task_target_count: number;
};

/** Normalize connection plan editor rows for API PATCH (shared by create-job + edit dialog). */
function normalizePlans(rows: ConnPlanEditorRow[]): NormalizedConnPlanRow[] {
  return rows
    .map((r) => {
      const d = connPlanTasksDigitsOnly(r.task_target_count);
      const n = d === "" ? Number.NaN : Math.floor(Number(d));
      return {
        connection_id: r.connection_id,
        task_target_count: n,
      };
    })
    .filter(
      (r): r is { connection_id: number; task_target_count: number } =>
        typeof r.connection_id === "number" &&
        r.connection_id >= 1 &&
        Number.isFinite(r.task_target_count) &&
        r.task_target_count >= 1,
    )
    .map((r) => ({
      connection_id: r.connection_id,
      task_target_count: Math.min(1000, Math.max(1, r.task_target_count)),
    }));
}

const MAJOR_FORM_LISTBOX = { style: { maxHeight: 280, overflow: "auto" as const } };

const filterMajorFormVendors = createFilterOptions<string>();
const filterChildDialogBots = createFilterOptions<BotRegistryItem>({
  stringify: (option) =>
    `${option.vendor} ${option.bot_slug} ${option.job_kind} ${option.job_kind}.${option.bot_slug}`,
});

const MAX_BATCH_TASKS = 30;

function connectionIdsForPlanRow(
  pool: number[],
  rows: ConnPlanEditorRow[],
  rowIndex: number,
): number[] {
  const row = rows[rowIndex];
  const cur =
    typeof row?.connection_id === "number" && row.connection_id >= 1 ? row.connection_id : null;
  const taken = new Set(
    rows.flatMap((r, j) =>
      j !== rowIndex && typeof r.connection_id === "number" && r.connection_id >= 1 ? [r.connection_id] : [],
    ),
  );
  return pool.filter((cid) => !taken.has(cid) || cid === cur);
}

function nextUnusedPoolConnectionId(
  poolSortedIds: number[],
  rows: ConnPlanEditorRow[],
): number | undefined {
  const used = new Set(
    rows
      .map((r) => (typeof r.connection_id === "number" ? r.connection_id : null))
      .filter((x): x is number => x != null && x >= 1),
  );
  return poolSortedIds.find((cid) => !used.has(cid));
}

function TabPanel(props: { children?: ReactNode; value: number | string; index: number | string }) {
  const { children, value, index } = props;
  const active = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!active}
      style={
        active
          ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
          : undefined
      }
    >
      {active ? (
        <Box
          sx={{
            pt: 2,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            // Always reserve the scrollbar gutter so the table doesn't shift
            // sideways the moment expanding a row makes the panel overflow.
            scrollbarGutter: "stable",
          }}
        >
          {children}
        </Box>
      ) : null}
    </div>
  );
}

/** Row of meta chips for the major-job header (user / action / vendor share one style). */
function MajorJobStripItem({ title, value, avatar }: { title: string; value: string; avatar?: boolean }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: avatar ? 1 : 0,
        px: 1.25,
        py: 0.85,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "grey.50"),
        border: 1,
        borderColor: "divider",
        boxShadow: (theme) => (theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(0,0,0,0.04)"),
      }}
    >
      {avatar ? (
        <Box sx={{ mt: 0.2 }}>
          <PersonOutline color="primary" sx={{ fontSize: 21, opacity: 0.9 }} />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1.1, letterSpacing: 0.06, fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.35 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}


function normalizeBotRegistryPayload(
  data: { results?: BotRegistryItem[] } | BotRegistryItem[] | undefined | null,
): BotRegistryItem[] {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

function normVendor(v: string | undefined | null): string {
  return (v ?? "").trim().toLowerCase();
}

/** Login line for tables/dropdowns under a major job — vendor omitted (shown on the major job header). */
function connectionPoolRowLabel(all: AdminVendorConnection[], connectionId: number): string {
  const c = all.find((x) => x.id === connectionId);
  if (!c) return "";
  const u = (c.username ?? "").trim();
  return u ? `@${u}` : "-";
}

function mjConnLabelVerbose(all: AdminVendorConnection[], connectionId: number): string {
  const short = connectionPoolRowLabel(all, connectionId);
  return short || "-";
}

/** Compose a 1-based ordinal label for a connection within a major job's pool. */
function mjConnLabelByOrdinal(
  poolIds: number[],
  all: AdminVendorConnection[],
  connectionId: number,
): string {
  const idx = poolIds.indexOf(connectionId);
  const ord = idx >= 0 ? idx + 1 : null;
  const short = mjConnLabelVerbose(all, connectionId);
  if (ord == null) return short;
  return `${ord}. ${short}`;
}

function extractDocsTypeFromOptions(additionalOptions: string): { types: string[]; remaining: string } {
  const parts = (additionalOptions || "").split(";").map((s) => s.trim()).filter(Boolean);
  const docsTypePart = parts.find((p) => p.startsWith("DOCS_TYPE="));
  const remaining = parts.filter((p) => !p.startsWith("DOCS_TYPE=")).join(";");
  if (docsTypePart) {
    const value = docsTypePart.slice("DOCS_TYPE=".length);
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length > 0) return { types: parsed.map(String), remaining };
    } catch {}
    return { types: [value], remaining };
  }
  return { types: [""], remaining: additionalOptions || "" };
}

function buildDeleteAdditionalOptions(baseOpts: string, docTypes: string[]): string {
  const validTypes = docTypes.filter((t) => t.trim());
  const docsTypePart = `DOCS_TYPE=${JSON.stringify(validTypes)}`;
  const { remaining } = extractDocsTypeFromOptions(baseOpts);
  return remaining ? `${remaining};${docsTypePart}` : docsTypePart;
}

function isSSNJob(row: Record<string, unknown>): boolean {
  return String(row.bot_name ?? "").startsWith("ssn_bots.");
}

/** Normalize em/en dashes in labels for display (ASCII hyphen with spaces). */
function displayJobNameShortDash(label: string): string {
  return label.replace(/\s*[\u2013\u2014]\s*/g, " - ");
}

function extractDefaultFolderSlugFromBotName(botName: string): string {
  const parts = botName.split(".");
  return parts[parts.length - 1] || "data";
}

type ChipColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

function taskRunStatusChipColor(status: string): ChipColor {
  const s = (status || "").toUpperCase();
  switch (s) {
    case "PENDING":
      return "warning";
    case "ECS_CONTAINER_STARTED":
      return "info";
    case "PROCESSING":
      return "primary";
    case "FINISHED":
      return "success";
    case "ERROR":
    case "CRITICAL_ERROR":
      return "error";
    case "STOPPED":
      return "warning";
    default:
      return "default";
  }
}

function botWorkStatusChipColor(status: string): ChipColor {
  const s = (status || "").toUpperCase();
  switch (s) {
    case "NOT_LOGGED_IN":
      return "default";
    case "2FA":
      return "warning";
    case "LOGGED_IN":
      return "info";
    case "PROCESSING":
      return "primary";
    case "FINISHED":
      return "success";
    case "ERROR":
    case "STOPPED":
      return "error";
    default:
      return "default";
  }
}

function StatusChipMj({
  label,
  color,
  title,
  onClick,
  icon,
}: {
  label: string;
  color: ChipColor;
  title?: string;
  onClick?: () => void;
  icon?: ReactElement;
}) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      color={color}
      title={title}
      onClick={onClick}
      icon={icon}
      sx={{
        height: 22,
        maxHeight: 22,
        fontWeight: 600,
        fontSize: "0.6875rem",
        lineHeight: 1,
        ...(onClick ? { cursor: "pointer", textDecoration: "underline" } : {}),
        "& .MuiChip-icon": { ml: 0.5, mr: -0.25, fontSize: "0.85rem" },
        "& .MuiChip-label": { px: 0.65, py: 0 },
      }}
    />
  );
}

/** Task row data as used in accordion (matches connection-tasks admin API + 2FA). */
export type MjConnectionTask = {
  id: number;
  job?: number | null;
  task_status: string;
  bot_work_status: string;
  task2fa_status?: string;
  bot_name: string;
  employee_number_from: number;
  employee_number_to: number;
  connection?: number;
};

const MJ_ACCORDION_ROW_SX = {
  "&:before": { display: "none" },
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  mb: 1,
  overflow: "hidden",
  transition: "none !important",
  "& .MuiCollapse-root": { transition: "none !important" },
  "&.Mui-expanded": { margin: 0, mb: 1, mt: 0 },
} as const;

const MJ_ACC_SUMMARY_SX = {
  minHeight: 48,
  py: 0,
  "&.Mui-expanded": { minHeight: 48 },
  "& .MuiAccordionSummary-content": {
    alignItems: "flex-start",
    margin: "10px 0 !important",
    "&.Mui-expanded": { margin: "10px 0 !important" },
  },
} as const;

/**
 * Task jobs table columns: [expand][checkbox][name][status][tasks][total][success][error][actions].
 * Fixed pixel widths (name flexes) keep header + collapsible rows perfectly aligned; the table keeps
 * this `minWidth` so the panel scrolls horizontally instead of squeezing the columns.
 */
const MJ_JOBS_TABLE_MIN_WIDTH = 920;
const MJ_TASKS_TABLE_MIN_WIDTH = 960;

const DIALOG_MINOR_SX = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
} as const;

type ManageTab = "jobs" | "ssn" | "connections";

// Compact 2FA action button so it doesn't tower over the icon buttons next to it.
const MJ_PASS_2FA_BTN_SX = {
  py: 0.15,
  px: 0.85,
  minWidth: 0,
  fontSize: "0.68rem",
  lineHeight: 1.4,
  "& .MuiButton-startIcon": {
    mr: 0.4,
    "& > *": { fontSize: "0.95rem" },
  },
} as const;

function canRunTaskByStatusMj(status: string) {
  return ["PENDING", "STOPPED", "FINISHED", "ERROR", "CRITICAL_ERROR"].includes(
    String(status ?? "").toUpperCase(),
  );
}

/** Run control is swapped for Stop while the worker is actually running workloads. */
function mjTaskShowsStopInsteadOfRun(status: string) {
  const s = String(status ?? "").toUpperCase();
  return ["ECS_CONTAINER_STARTED", "PROCESSING"].includes(s);
}

function mjJobAggregateRunning(job: Record<string, unknown>): boolean {
  /** Use stats only: they aggregate live Task rows. Job.task_status can lag after stop/run. */
  return mjJobStatsTotals(job).running > 0;
}

function parseConnectionPlansFromJobRow(
  job: Record<string, unknown>,
): { connection_id: number; task_target_count: number }[] {
  const raw = job.connection_plans;
  if (!Array.isArray(raw)) return [];
  const out: { connection_id: number; task_target_count: number }[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const cid = Number(obj.connection_id);
    if (!Number.isFinite(cid) || cid < 1) continue;
    out.push({
      connection_id: cid,
      task_target_count: Math.max(1, Number(obj.task_target_count) || 1),
    });
  }
  return out;
}

function legacyPrimaryConnectionFromJob(job: Record<string, unknown>): number | null {
  const direct = Number(job.connection_id ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const cr = job.connection;
  if (typeof cr === "number" && Number.isFinite(cr) && cr > 0) return cr;
  if (cr != null && typeof cr === "object") {
    const pid = Number((cr as Record<string, unknown>).id ?? 0);
    if (Number.isFinite(pid) && pid > 0) return pid;
  }
  return null;
}

function mjTasksCountOnConnection(tasks: MjConnectionTask[], connectionId: number): number {
  return tasks.reduce((acc, t) => acc + (Number(t.connection) === connectionId ? 1 : 0), 0);
}

/** Sums numeric per-row "to add" counts grouped by login (planned add dialog). */
function aggregatePlannedAddsByConnection(rows: ConnPlanEditorRow[]): { cid: number; add: number }[] {
  const m = new Map<number, number>();
  for (const r of rows) {
    const cid = typeof r.connection_id === "number" ? r.connection_id : NaN;
    const d = connPlanTasksDigitsOnly(r.task_target_count);
    const addRaw = d === "" ? Number.NaN : Math.floor(Number(d));
    const add = Number.isFinite(addRaw) ? addRaw : 0;
    if (!Number.isFinite(cid) || cid < 1 || add < 1) continue;
    m.set(cid, (m.get(cid) ?? 0) + add);
  }
  return Array.from(m.entries())
    .map(([cid, add]) => ({ cid, add }))
    .sort((a, b) => a.cid - b.cid);
}

function resolveMjBulkTaskAddPayload(
  planMode: boolean,
  plansSnap: { connection_id: number; task_target_count: number }[],
  poolIds: number[],
  rows: ConnPlanEditorRow[],
  currentTasks: MjConnectionTask[],
  jobTotalRemain: number,
  connections: AdminVendorConnection[],
):
  | { error: string }
  | { planMode: false; legacyCount: number }
  | { planMode: true; batches: { connection_id: number; count: number }[] } {
  if (jobTotalRemain < 1) {
    return { error: `This job already has ${MAX_BATCH_TASKS} tasks (maximum).` };
  }

  if (!planMode) {
    if (rows.length !== 1) {
      return { error: "This job expects a single add row." };
    }
    const d0 = connPlanTasksDigitsOnly(String(rows[0]?.task_target_count ?? ""));
    const n = d0 === "" ? Number.NaN : Math.floor(Number(d0));
    const parsed = Number.isFinite(n) ? n : 0;
    if (parsed < 1) return { error: "Add at least one task." };
    if (parsed > jobTotalRemain) return { error: `At most ${jobTotalRemain} more task(s) fit on this job.` };
    return { planMode: false, legacyCount: parsed };
  }

  // Plan rows are treated as advisory hints in this UI: when a row asks for
  // more tasks than the recorded quota, the backend auto-raises the quota for
  // that connection. The pool set (logins available to this major job) is
  // still enforced so users cannot attach tasks to logins outside the pool.
  const poolOk = poolIds.length ? new Set(poolIds) : null;
  // Mark unused variable to keep the helper signature backwards compatible.
  void plansSnap;
  void currentTasks;
  void connections;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const cid = typeof r.connection_id === "number" ? r.connection_id : NaN;
    const d = connPlanTasksDigitsOnly(String(r.task_target_count ?? ""));
    const addRaw = d === "" ? Number.NaN : Math.floor(Number(d));
    const add = Number.isFinite(addRaw) ? addRaw : 0;
    if (!Number.isFinite(cid) || cid < 1) {
      return { error: `Row ${i + 1}: choose a login.` };
    }
    if (poolOk && !poolOk.has(cid)) {
      return { error: `Row ${i + 1}: login outside the mapped set.` };
    }
    if (add < 1) {
      return { error: `Row ${i + 1}: enter at least 1 task to add.` };
    }
  }

  const batches = aggregatePlannedAddsByConnection(rows).map(({ cid, add }) => ({
    connection_id: cid,
    count: add,
  }));
  if (!batches.length) {
    return { error: "Pick logins and how many tasks to add on each row." };
  }

  const sum = batches.reduce((acc, b) => acc + b.count, 0);
  if (sum > jobTotalRemain) {
    return { error: `Total adds (${sum}) exceed the ${MAX_BATCH_TASKS}-task job limit (${jobTotalRemain} left).` };
  }

  return { planMode: true, batches };
}

function mjJobDisplayName(job: Record<string, unknown>): string {
  return displayJobNameShortDash(String(job.name ?? ""));
}

function mjEmployeeStatValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "—";
}

function mjEmployeeStatsFromJob(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  const total = stats?.actual_total_employees ?? job.actual_total_employees;
  const success = stats?.success_employees_count;
  const error = stats?.error_employees_count;
  const snapshotAt =
    typeof stats?.stats_snapshot_updated_at === "string"
      ? (stats.stats_snapshot_updated_at as string)
      : null;
  const hasAny =
    total != null ||
    (success != null && Number(success) > 0) ||
    (error != null && Number(error) > 0);
  if (!hasAny) {
    return { total: "—", success: "—", error: "—", snapshotAt };
  }
  return {
    total: mjEmployeeStatValue(total),
    success: mjEmployeeStatValue(success ?? 0),
    error: mjEmployeeStatValue(error ?? 0),
    snapshotAt,
  };
}

function mjStatsSnapshotTooltip(snapshotAt: string | null | undefined): string {
  if (!snapshotAt) return "Counts include only currently existing tasks. Refresh to scan stored logs (incl. deleted tasks).";
  const ts = new Date(snapshotAt);
  if (Number.isNaN(ts.getTime())) return "Stats are based on stored logs.";
  const diffMs = Date.now() - ts.getTime();
  const min = Math.round(diffMs / 60000);
  let when: string;
  if (min < 1) when = "just now";
  else if (min < 60) when = `${min} min ago`;
  else if (min < 60 * 24) when = `${Math.round(min / 60)} h ago`;
  else when = `${Math.round(min / (60 * 24))} d ago`;
  return `Includes deleted tasks. Refreshed from stored logs ${when} (${ts.toLocaleString()}).`;
}

function mjArtifactRangeWarning(warning: string | undefined | null): string | null {
  const trimmed = (warning ?? "").trim();
  if (!trimmed) return null;
  if (/employees_info\.json has not been created/i.test(trimmed)) return null;
  return trimmed;
}

function mjEmployeeProgressSummary(job: Record<string, unknown>): string {
  const stats = job.stats as Record<string, unknown> | undefined;
  const processed =
    Number(stats?.success_employees_count ?? 0) + Number(stats?.error_employees_count ?? 0);
  const total = job.actual_total_employees;
  if (processed === 0 && (total === undefined || total === null)) return "unknown";
  if (total === undefined || total === null) return `${processed}/unknown`;
  return `${processed}/${total}`;
}

function mjJobStatsTotals(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  const running = Number(stats?.running_tasks ?? 0);
  const total = Number(stats?.total_tasks ?? 0);
  return { running, total };
}

function normalizeRawTaskToMj(t: Record<string, unknown>, fallbackJobId: number): MjConnectionTask {
  const jobVal = t.job;
  const jid =
    typeof jobVal === "number"
      ? jobVal
      : jobVal != null && typeof jobVal === "object" && "id" in (jobVal as object)
        ? Number((jobVal as { id?: unknown }).id)
        : fallbackJobId;
  const connVal = t.connection;
  const cid =
    typeof connVal === "number"
      ? connVal
      : connVal != null && typeof connVal === "object" && "id" in (connVal as object)
        ? Number((connVal as { id?: unknown }).id)
        : undefined;
  return {
    id: Number(t.id),
    job: Number.isFinite(jid) ? jid : fallbackJobId,
    task_status: String(t.task_status ?? ""),
    bot_work_status: String(t.bot_work_status ?? ""),
    task2fa_status: typeof t.task2fa_status === "string" ? t.task2fa_status : "not_requested",
    bot_name: String(t.bot_name ?? ""),
    employee_number_from: Number(t.employee_number_from ?? 0),
    employee_number_to: Number(t.employee_number_to ?? 0),
    connection: cid,
  };
}

function botNameFromRegistry(row: BotRegistryItem | { vendor: string; job_kind: string; bot_slug: string }) {
  return `${row.job_kind}.${row.bot_slug}`;
}

function getErr(error: unknown, fallback: string): string {
  const stringify = (data: unknown): string | undefined => {
    if (data == null || data === "") return undefined;
    if (typeof data === "string") return data;
    if (typeof data === "number" || typeof data === "boolean") return String(data);
    if (Array.isArray(data)) {
      const parts = data.map((x) => stringify(x)).filter(Boolean) as string[];
      return parts.length ? parts.join(" ") : undefined;
    }
    if (typeof data === "object") {
      const o = data as Record<string, unknown>;
      for (const key of ["detail", "message", "error"]) {
        const inner = o[key];
        const s = stringify(inner);
        if (s) return s;
      }
      const parts: string[] = [];
      for (const [k, v] of Object.entries(o)) {
        if (k === "detail" || k === "message" || k === "error") continue;
        const s = stringify(v);
        if (s) parts.push(`${k}: ${s}`);
      }
      if (parts.length) return parts.join("; ");
    }
    return undefined;
  };
  const wrapped = error as { data?: unknown } | undefined;
  return stringify(wrapped?.data) || fallback;
}

export type MajorJobsPanelProps = {
  userId: number;
  userLabel?: string;
  connections: AdminVendorConnection[];
  connectionsLoading?: boolean;
  connectionsError?: boolean;
  onRefreshConnections: () => Promise<unknown>;
  /** Full-page workspace for one major job (opened via route). */
  detailMajorJobId?: number | null;
  onExitMajorDetail?: () => void;
  /** Opens major job workspace - typically navigate to `/bots-management/:userId/major-job/:id`. */
  onOpenMajorDetail?: (majorJobId: number) => void;
  /** Switches the page to the standalone Connections workspace for this user. */
  onOpenConnectionsScreen?: () => void;
};

/** Major jobs + task jobs for one Django user (vendor-scoped pools). */
export default function MajorJobsPanel({
  userId,
  userLabel,
  connections,
  connectionsLoading = false,
  connectionsError = false,
  onRefreshConnections,
  detailMajorJobId = null,
  onExitMajorDetail,
  onOpenMajorDetail,
  onOpenConnectionsScreen,
}: MajorJobsPanelProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const canEditBots = Boolean(useAppSelector((s) => s.auth.user?.platform_profile?.is_superuser));
  const { data: majorsRaw, refetch, isFetching: majorsFetching } = useGetMajorJobsQuery(userId, {
    refetchOnMountOrArgChange: true,
  });
  const majors = useMemo(() => {
    if (majorsRaw == null) return [];
    if (Array.isArray(majorsRaw)) return majorsRaw;
    const r = (majorsRaw as { results?: MajorJob[] }).results;
    return Array.isArray(r) ? r : [];
  }, [majorsRaw]);
  const [createMajorJob, { isLoading: isCreating }] = useCreateMajorJobMutation();
  const [updateMajorJob, { isLoading: isSavingMajor }] = useUpdateMajorJobMutation();
  const [deleteMajorJob, { isLoading: isDeletingMajor }] = useDeleteMajorJobMutation();

  const [createConnectionMutation, { isLoading: isMpcCreating }] =
    useCreateBackendUserBotConnectionMutation();
  const [updateConnectionMutation, { isLoading: isMpcUpdating }] =
    useUpdateConnectionMutation();
  const [deleteConnectionMutation, { isLoading: isMpcDeleting }] =
    useDeleteConnectionMutation();

  const [selectedMajorIds, setSelectedMajorIds] = useState<number[]>([]);
  const [activeMajorId, setActiveMajorId] = useState<number | null>(null);

  const [majorEditOpen, setMajorEditOpen] = useState(false);
  const [majorEditTarget, setMajorEditTarget] = useState<MajorJob | null>(null);
  const [majName, setMajName] = useState("");
  const [majVendor, setMajVendor] = useState("");
  const [majKind, setMajKind] = useState("download_bots");
  const [majorFormError, setMajorFormError] = useState<string | null>(null);
  const [majorRowDeleteTarget, setMajorRowDeleteTarget] = useState<MajorJob | null>(null);
  const [majorBulkDeleteOpen, setMajorBulkDeleteOpen] = useState(false);

  const { data: majorFormAllRegistry } = useGetBotRegistryQuery(
    { isActive: true, pageSize: 500 },
    { skip: !majorEditOpen },
  );
  const registryVendorsBase = useMemo(() => {
    const rows = normalizeBotRegistryPayload(majorFormAllRegistry);
    return Array.from(new Set(rows.map((r) => r.vendor))).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [majorFormAllRegistry]);

  const majorFormVendorOptions = useMemo(() => {
    const v = majVendor.trim();
    if (!v) return registryVendorsBase;
    const hit = registryVendorsBase.some((x) => x.toLowerCase() === v.toLowerCase());
    if (!hit) return [...registryVendorsBase, v].sort((a, b) => a.localeCompare(b));
    return registryVendorsBase;
  }, [registryVendorsBase, majVendor]);

  const { data: childJobsData, refetch: refetchChildJobs } = useGetTaskJobsQuery(
    { userId, majorJobId: activeMajorId ?? undefined },
    // Refetch on remount so returning from a task detail shows fresh task-job data.
    { skip: activeMajorId == null, refetchOnMountOrArgChange: true },
  );
  const childJobs = useMemo(() => {
    if (Array.isArray(childJobsData)) return childJobsData;
    const r = (childJobsData as { results?: unknown[] } | undefined)?.results;
    return Array.isArray(r) ? r : [];
  }, [childJobsData]);

  /** Safe refresh — `refetch()` throws if the subscribed query was skipped (no active major yet). */
  const refreshChildJobsList = useCallback(async () => {
    const majorJobId = activeMajorId ?? detailMajorJobId ?? null;
    if (majorJobId == null) return;
    try {
      await dispatch(
        adminUsersApi.endpoints.getTaskJobs.initiate(
          { userId, majorJobId },
          { forceRefetch: true },
        ),
      ).unwrap();
    } catch {
      // ignore — list may not be mounted yet
    }
  }, [activeMajorId, detailMajorJobId, userId, dispatch]);

  const safeRefetchChildJobs = useCallback(async () => {
    if (activeMajorId == null) {
      await refreshChildJobsList();
      return;
    }
    try {
      await refetchChildJobs();
    } catch {
      await refreshChildJobsList();
    }
  }, [activeMajorId, refetchChildJobs, refreshChildJobsList]);

  const [createTasksBatch] = useCreateTasksBatchMutation();
  const [updateTaskJob, { isLoading: isMjEditJobSaving }] = useUpdateTaskJobMutation();
  const [taskJobsBatchDelete, { isLoading: isDeletingJobsBulk }] =
    useTaskJobsBatchDeleteMutation();

  const [triggerJobLogsArchive, { isFetching: isMjLogsArchiveLoading }] =
    useLazyGetJobLogsArchiveQuery();
  const [clearJobLogsMut, { isLoading: isMjClearLogsLoading }] = useClearJobLogsMutation();
  const [mjClearLogsJobId, setMjClearLogsJobId] = useState<number | null>(null);

  const [deleteTaskJobMut, { isLoading: isMjDeletingJob }] = useDeleteTaskJobMutation();
  const [deleteTaskMutation, { isLoading: isDeletingNestedTask }] = useDeleteTaskMutation();
  const [tasksBatchDeleteNestMut, { isLoading: isMjNestedTasksBatchDeleting }] = useTasksBatchDeleteMutation();
  const [addTasksToJobMutation, { isLoading: isAddingTasksToJob }] = useAddTasksToJobMutation();
  const [runTaskMutation, { isLoading: isRunTaskBusy }] = useRunTaskMutation();
  const [stopTaskMutation, { isLoading: isStopTaskBusy }] = useStopTaskMutation();
  const [updateAdminTaskMut, { isLoading: isUpdatingTaskRow }] = useUpdateAdminTaskMutation();
  const [setTaskPendingMutation, { isLoading: isSetTaskPendingBusy }] = useSetTaskPendingMutation();
  const [runTasksBatchMut, { isLoading: isMjBatchRunning }] = useRunTasksBatchMutation();
  const [stopTasksBatchMut, { isLoading: isMjBatchStopping }] = useStopTasksBatchMutation();
  const [submitTask2FACodeMut, { isLoading: isSubmit2FAMj }] = useSubmitTask2FACodeMutation();
  const [expandedMjJobAccordion, setExpandedMjJobAccordion] = useState<Record<number, boolean>>({});
  const [mjAccordionTasksByJob, setMjAccordionTasksByJob] = useState<Record<number, MjConnectionTask[]>>({});
  const [mjAccordionLoadingJobId, setMjAccordionLoadingJobId] = useState<number | null>(null);
  /** Timestamp (ms) of last successful accordion tasks fetch — used for 30-second cache. */
  const [mjAccordionCacheTs, setMjAccordionCacheTs] = useState<Record<number, number>>({});
  /** Task ID that is currently being run or stopped (for per-row spinner). */
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  /** Coverage / employees-info warnings keyed by task job id (from API stats). */
  const [mjJobRangeWarningsByJobId, setMjJobRangeWarningsByJobId] = useState<Record<number, string>>({});
  /** Run/stop/delete errors shown inside each job accordion row. */
  const [mjJobActionAlertsByJobId, setMjJobActionAlertsByJobId] = useState<Record<number, string>>({});
  const [mjRefreshingJobIds, setMjRefreshingJobIds] = useState<Record<number, boolean>>({});
  const [mjJobDetailById, setMjJobDetailById] = useState<Record<number, Record<string, unknown>>>({});
  const [mjWsConnected, setMjWsConnected] = useState(() => botsManagementWs.isConnected());
  const mjWsDebounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const expandedMjJobAccordionRef = useRef<Record<number, boolean>>({});
  const mjAccordionTasksByJobRef = useRef<Record<number, MjConnectionTask[]>>({});
  const reloadMjAccordionTasksRef = useRef<(jobId: number) => Promise<MjConnectionTask[] | null>>(
    async () => null,
  );
  const refreshMjJobDetailRef = useRef<(jobId: number, refreshList?: boolean) => Promise<void>>(
    async () => undefined,
  );
  /** Tracks job IDs already prefetched so we don't repeat on every render. */
  const prefetchedJobIdsRef = useRef<Set<number>>(new Set());
  /** Job IDs with an accordion reload currently in flight — dedupes rapid open/close/open. */
  const mjAccordionInFlightRef = useRef<Set<number>>(new Set());

  const [mjManageJobId, setMjManageJobId] = useState<number | null>(null);
  const [mjManageJobDetail, setMjManageJobDetail] = useState<Record<string, unknown> | null>(null);
  const [mjManageJobError, setMjManageJobError] = useState<string | null>(null);

  const [mjAddTasksOpen, setMjAddTasksOpen] = useState(false);
  const [mjAddTasksJobId, setMjAddTasksJobId] = useState<number | null>(null);
  const [mjAddTasksLoading, setMjAddTasksLoading] = useState(false);
  const [mjBulkAddRows, setMjBulkAddRows] = useState<ConnPlanEditorRow[]>([
    { connection_id: "", task_target_count: "1" },
  ]);
  const [mjAddPlanConnPool, setMjAddPlanConnPool] = useState<number[]>([]);
  const [mjAddTasksErr, setMjAddTasksErr] = useState<string | null>(null);

  const [mjDeleteJobId, setMjDeleteJobId] = useState<number | null>(null);
  const [mjDeleteJobLogs, setMjDeleteJobLogs] = useState(false);
  const [bulkChildDeleteLogs, setBulkChildDeleteLogs] = useState(false);
  const [majorDeleteLogs, setMajorDeleteLogs] = useState(false);

  const [mjEditJobOpen, setMjEditJobOpen] = useState(false);
  const [mjEditJobId, setMjEditJobId] = useState<number | null>(null);
  const [mjEditBaselineFrom, setMjEditBaselineFrom] = useState(0);
  const [mjEditJobName, setMjEditJobName] = useState("");
  const [mjEditBotName, setMjEditBotName] = useState("");
  const [mjEditEmployeeTo, setMjEditEmployeeTo] = useState("");
  const [mjEditDocumentType, setMjEditDocumentType] = useState("");
  const [mjEditDocumentTypes, setMjEditDocumentTypes] = useState<string[]>([" "]);
  const [mjEditAdditionalOptions, setMjEditAdditionalOptions] = useState("");
  const [mjEditProcessErrors, setMjEditProcessErrors] = useState(false);
  const [mjEditUploadAsZip, setMjEditUploadAsZip] = useState(false);
  const [mjEditUnzipBefore, setMjEditUnzipBefore] = useState(false);
  const [mjEditUseSsnMatch, setMjEditUseSsnMatch] = useState(true);
  const [mjEditSourcePath, setMjEditSourcePath] = useState("");
  const [mjEditJobError, setMjEditJobError] = useState<string | null>(null);

  const [mjDeleteSingleTaskId, setMjDeleteSingleTaskId] = useState<number | null>(null);
  /** Task ids selected inside a child job accordion (for batch delete). */
  const [mjNestedTaskSelectionByJob, setMjNestedTaskSelectionByJob] = useState<Record<number, number[]>>({});
  const [mjBatchDeleteNestDialog, setMjBatchDeleteNestDialog] = useState<{ jobId: number; ids: number[] } | null>(null);
  const [twoFaMjTask, setTwoFaMjTask] = useState<MjConnectionTask | null>(null);
  const [twoFaMjCode, setTwoFaMjCode] = useState("");
  const [twoFaMjErr, setTwoFaMjErr] = useState<string | null>(null);

  // Logs dialog (Status logs + File logs tabs)
  const [statusLogsOpen, setStatusLogsOpen] = useState(false);
  const [statusLogsJobId, setStatusLogsJobId] = useState<number | null>(null);
  const [statusLogsJobName, setStatusLogsJobName] = useState<string>("");
  const [statusLogsCache, setStatusLogsCache] = useState<Record<number, { logs: string; fetchedAt: number }>>({});
  const [statusLogsRefreshing, setStatusLogsRefreshing] = useState(false);
  const [fileLogsCache, setFileLogsCache] = useState<Record<number, { logs: string; fetchedAt: number }>>({});
  const [logsTab, setLogsTab] = useState<"status" | "file">("status");
  const statusLogsPreRef = useRef<HTMLElement | null>(null);

  // Error popup (clickable CRITICAL_ERROR status chip)
  const [mjErrorPopupTask, setMjErrorPopupTask] = useState<{ jobId: number; task: MjConnectionTask } | null>(null);

  // Matches tab (inside Manage Job dialog, upload_bots only)
  const [mjManageTab, setMjManageTab] = useState(0);
  type MatchesFileInfo = { filename: string; exists: boolean; size_bytes: number | null; source: string | null };
  const [matchesFiles, setMatchesFiles] = useState<MatchesFileInfo[] | null>(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  // CSV viewer popup
  const [matchesViewOpen, setMatchesViewOpen] = useState(false);
  const [matchesViewFilename, setMatchesViewFilename] = useState("");
  const [matchesViewRows, setMatchesViewRows] = useState<string[][]>([]);
  const [matchesViewLoading, setMatchesViewLoading] = useState(false);
  // Delete confirmation
  const [matchesDeleteFilename, setMatchesDeleteFilename] = useState<string | null>(null);
  const [matchesDeleting, setMatchesDeleting] = useState(false);
  const [matchesSelectedFiles, setMatchesSelectedFiles] = useState<string[]>([]);
  const [matchesMergeOpen, setMatchesMergeOpen] = useState(false);
  const [matchesMergeFile, setMatchesMergeFile] = useState<File | null>(null);
  const [matchesMergeStagingKey, setMatchesMergeStagingKey] = useState<string | null>(null);
  const [matchesMergeStep, setMatchesMergeStep] = useState<
    "idle" | "uploading" | "ready" | "merging" | "error"
  >("idle");
  const [matchesMergeError, setMatchesMergeError] = useState<string | null>(null);
  const [matchesMergeSuccess, setMatchesMergeSuccess] = useState<string | null>(null);
  const [triggerGetMatchesFileContent] = useLazyGetJobMatchesFileContentQuery();
  const [deleteMatchesFileMut] = useDeleteJobMatchesFileMutation();
  const [presignMatchesMergeMut] = usePresignJobMatchesMergeUploadMutation();
  const [mergeMatchesReportsMut] = useMergeJobMatchesReportsMutation();

  // ── SSN Job tab ────────────────────────────────────────────────────────────
  type SsnJobData = {
    job: Record<string, unknown> | null;
    task: Record<string, unknown> | null;
    ssn_file_exists: boolean | null;
  };
  const [ssnJobData, setSsnJobData] = useState<SsnJobData | null>(null);
  const [ssnJobLoading, setSsnJobLoading] = useState(false);
  const [ssnJobError, setSsnJobError] = useState<string | null>(null);
  const [ssnJobSelectedConnId, setSsnJobSelectedConnId] = useState<number | null>(null);
  const [ssnJobCreating, setSsnJobCreating] = useState(false);
  const [ssnJobDeleting, setSsnJobDeleting] = useState(false);
  const [ssnJobFileDeleting, setSsnJobFileDeleting] = useState(false);
  const [ssnJobDeleteConfirmOpen, setSsnJobDeleteConfirmOpen] = useState(false);
  const [ssnAccordionExpanded, setSsnAccordionExpanded] = useState(false);
  const [createSsnJobMut] = useCreateMajorJobSsnJobMutation();
  const [deleteSsnJobMut] = useDeleteMajorJobSsnJobMutation();
  const [deleteSsnFileMut] = useDeleteMajorJobSsnFileMutation();
  const [triggerGetSsnFileContent] = useLazyGetMajorJobSsnFileContentQuery();
  const [ssnFileViewOpen, setSsnFileViewOpen] = useState(false);
  const [ssnFileViewLoading, setSsnFileViewLoading] = useState(false);
  const [ssnFileViewContent, setSsnFileViewContent] = useState<string | null>(null);
  const ssnJobDataRef = useRef<SsnJobData | null>(null);
  const loadSsnJobRef = useRef<(majorJobId: number, opts?: { silent?: boolean }) => Promise<void>>(async () => {});
  const ssnMajorIdRef = useRef<number | null>(null);

  const loadSsnJob = async (majorJobId: number, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      setSsnJobLoading(true);
      setSsnJobError(null);
      setSsnJobData(null);
    }
    try {
      const res = await dispatch(
        adminUsersApi.endpoints.getMajorJobSsnJob.initiate(majorJobId, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      const next = res as SsnJobData;
      setSsnJobData((prev) => {
        if (prev?.ssn_file_exists !== next.ssn_file_exists) void refetch();
        return next;
      });
    } catch (e: unknown) {
      if (!silent) {
        const msg = (e as { data?: { message?: string } })?.data?.message ?? "Failed to load SSN job.";
        setSsnJobError(msg);
      }
    } finally {
      if (!silent) setSsnJobLoading(false);
    }
  };

  /** Inline draft connection IDs for tasks shown in the per-job accordion table.
   *  Shape: { [taskId]: pendingConnectionId }. A row shows a Save button while
   *  the draft differs from the task's persisted connection. */
  const [taskConnDraftById, setTaskConnDraftById] = useState<Record<number, number>>({});
  const [taskConnSavingId, setTaskConnSavingId] = useState<number | null>(null);
  const [taskConnSaveErrById, setTaskConnSaveErrById] = useState<Record<number, string>>({});

  const setTaskConnDraft = useCallback((taskId: number, cid: number) => {
    setTaskConnDraftById((prev) => ({ ...prev, [taskId]: cid }));
    setTaskConnSaveErrById((prev) => {
      if (!prev[taskId]) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const clearTaskConnDraft = useCallback((taskId: number) => {
    setTaskConnDraftById((prev) => {
      if (prev[taskId] == null) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    setTaskConnSaveErrById((prev) => {
      if (prev[taskId] == null) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const saveTaskConn = useCallback(
    async (jobId: number, task: MjConnectionTask, newCid: number) => {
      if (!canEditBots) return;
      setTaskConnSavingId(task.id);
      try {
        await updateAdminTaskMut({ taskId: task.id, body: { connection: newCid } }).unwrap();
        setMjAccordionTasksByJob((prev) => {
          const list = prev[jobId];
          if (!list) return prev;
          return {
            ...prev,
            [jobId]: list.map((t) => (t.id === task.id ? { ...t, connection: newCid } : t)),
          };
        });
        clearTaskConnDraft(task.id);
      } catch (e) {
        setTaskConnSaveErrById((prev) => ({
          ...prev,
          [task.id]: getErr(e, "Failed to move the task to the selected connection."),
        }));
      } finally {
        setTaskConnSavingId(null);
      }
    },
    [canEditBots, updateAdminTaskMut, clearTaskConnDraft],
  );

  const mjJobActionBusy =
    isAddingTasksToJob ||
    isRunTaskBusy ||
    isStopTaskBusy ||
    isSetTaskPendingBusy ||
    isDeletingNestedTask ||
    isMjNestedTasksBatchDeleting ||
    isMjBatchRunning ||
    isMjBatchStopping ||
    isSubmit2FAMj;

  /** Generalized logs fetcher shared by the Status logs + File logs tabs. */
  const fetchJobLogs = async (
    tab: "status" | "file",
    jobId: number,
    cache: Record<number, { logs: string; fetchedAt: number }>,
    setCache: Dispatch<
      SetStateAction<Record<number, { logs: string; fetchedAt: number }>>
    >,
    forceRefresh: boolean,
  ) => {
    const cached = cache[jobId];
    const stale = !cached || Date.now() - cached.fetchedAt > 30_000;
    if (!stale && !forceRefresh) return;
    // Clear old content immediately so the user sees a loading state, not stale data.
    setCache((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    setStatusLogsRefreshing(true);
    try {
      // Use dispatch+initiate with forceRefetch so RTK Query never returns a cached response.
      const endpoint =
        tab === "file"
          ? adminUsersApi.endpoints.getJobFileLogs
          : adminUsersApi.endpoints.getJobStatusLogs;
      const res = await dispatch(
        endpoint.initiate(jobId, {
          subscribe: false,
          forceRefetch: true,
        }),
      ).unwrap();
      setCache((prev) => ({
        ...prev,
        [jobId]: { logs: res.logs, fetchedAt: Date.now() },
      }));
    } catch {
      setCache((prev) => ({
        ...prev,
        [jobId]: { logs: "(failed to load logs)", fetchedAt: Date.now() },
      }));
    } finally {
      setStatusLogsRefreshing(false);
    }
  };

  const openStatusLogs = async (jobId: number, jobName?: string, forceRefresh = false) => {
    setStatusLogsJobId(jobId);
    if (jobName !== undefined) setStatusLogsJobName(jobName);
    setLogsTab("status");
    setStatusLogsOpen(true);
    await fetchJobLogs("status", jobId, statusLogsCache, setStatusLogsCache, forceRefresh);
  };

  const openFileLogs = async (jobId: number, forceRefresh = false) => {
    await fetchJobLogs("file", jobId, fileLogsCache, setFileLogsCache, forceRefresh);
  };

  // Scroll logs pre to the bottom whenever new content lands.
  useEffect(() => {
    if (statusLogsRefreshing) return; // wait until refresh is done
    const el = statusLogsPreRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [statusLogsRefreshing, statusLogsCache, fileLogsCache, logsTab]);

  // --- Matches files helpers ---

  const loadMatchesFiles = async (jobId: number) => {
    setMatchesLoading(true);
    setMatchesError(null);
    setMatchesSelectedFiles([]);
    try {
      const data = await dispatch(
        adminUsersApi.endpoints.getJobMatchesFiles.initiate(jobId, {
          subscribe: false,
          forceRefetch: true,
        }),
      ).unwrap();
      setMatchesFiles(data);
    } catch {
      setMatchesError("Failed to load matches files.");
    } finally {
      setMatchesLoading(false);
    }
  };

  const openMatchesFileViewer = async (jobId: number, filename: string) => {
    setMatchesViewFilename(filename);
    setMatchesViewRows([]);
    setMatchesViewOpen(true);
    setMatchesViewLoading(true);
    try {
      const res = await triggerGetMatchesFileContent({ jobId, filename }, false).unwrap();
      // Parse CSV: split by lines, then by comma
      const lines = res.content.split(/\r?\n/).filter((l) => l.trim() !== "");
      const parsed = lines.map((line) => {
        // Simple CSV parse: handle quoted fields
        const cols: string[] = [];
        let cur = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            inQuotes = !inQuotes;
          } else if (ch === "," && !inQuotes) {
            cols.push(cur);
            cur = "";
          } else {
            cur += ch;
          }
        }
        cols.push(cur);
        return cols;
      });
      setMatchesViewRows(parsed);
    } catch {
      setMatchesViewRows([["(failed to load file content)"]]);
    } finally {
      setMatchesViewLoading(false);
    }
  };

  const downloadMatchesFile = async (jobId: number, filename: string) => {
    try {
      const res = await triggerGetMatchesFileContent({ jobId, filename }, false).unwrap();
      const blob = new Blob([res.content], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will see no download
    }
  };

  const openSsnFileViewer = async (majorJobId: number) => {
    setSsnFileViewOpen(true);
    setSsnFileViewLoading(true);
    setSsnFileViewContent(null);
    try {
      const res = await triggerGetSsnFileContent(majorJobId, false).unwrap();
      let pretty = res.content;
      try {
        pretty = JSON.stringify(JSON.parse(res.content), null, 2);
      } catch {
        /* keep raw content when it is not valid JSON */
      }
      setSsnFileViewContent(pretty);
    } catch {
      setSsnFileViewContent("(failed to load file content)");
    } finally {
      setSsnFileViewLoading(false);
    }
  };

  const downloadSsnFile = async (majorJobId: number) => {
    try {
      const res = await triggerGetSsnFileContent(majorJobId, false).unwrap();
      const blob = new Blob([res.content], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || "ssn_info.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will see no download
    }
  };

  const confirmDeleteMatchesFile = async (jobId: number) => {
    if (!canEditBots) return;
    if (!matchesDeleteFilename || jobId == null) return;
    setMatchesDeleting(true);
    try {
      await deleteMatchesFileMut({ jobId, filename: matchesDeleteFilename }).unwrap();
      setMatchesDeleteFilename(null);
      await loadMatchesFiles(jobId);
    } catch {
      // keep dialog open on error
    } finally {
      setMatchesDeleting(false);
    }
  };

  const confirmBatchDeleteMatchesFiles = async (jobId: number) => {
    if (!canEditBots) return;
    if (!matchesSelectedFiles.length) return;
    setMatchesDeleting(true);
    try {
      await Promise.all(
        matchesSelectedFiles.map((filename) => deleteMatchesFileMut({ jobId, filename }).unwrap()),
      );
      setMatchesSelectedFiles([]);
      await loadMatchesFiles(jobId);
    } catch {
      // keep selection visible so user can retry
    } finally {
      setMatchesDeleting(false);
    }
  };

  const resetMatchesMergeState = () => {
    setMatchesMergeOpen(false);
    setMatchesMergeFile(null);
    setMatchesMergeStagingKey(null);
    setMatchesMergeStep("idle");
    setMatchesMergeError(null);
  };

  const openMatchesMergeDialog = () => {
    setMatchesMergeFile(null);
    setMatchesMergeStagingKey(null);
    setMatchesMergeStep("idle");
    setMatchesMergeError(null);
    setMatchesMergeOpen(true);
  };

  const handleMatchesMergeFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!canEditBots) return;
    const file = e.target.files?.[0];
    if (!file || mjManageJobId == null) return;
    e.target.value = "";

    setMatchesMergeFile(file);
    setMatchesMergeStagingKey(null);
    setMatchesMergeStep("uploading");
    setMatchesMergeError(null);

    try {
      const presign = await presignMatchesMergeMut({
        jobId: mjManageJobId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
      }).unwrap();

      const uploadResponse = await fetch(presign.upload_url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      setMatchesMergeStagingKey(presign.key);
      setMatchesMergeStep("ready");
    } catch (err: unknown) {
      setMatchesMergeStep("error");
      const msg =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : "Failed to upload file.");
      setMatchesMergeError(msg);
    }
  };

  const confirmMatchesMerge = async () => {
    if (!canEditBots) return;
    if (!matchesMergeStagingKey || mjManageJobId == null) return;
    setMatchesMergeStep("merging");
    setMatchesMergeError(null);
    try {
      const res = await mergeMatchesReportsMut({
        jobId: mjManageJobId,
        staging_key: matchesMergeStagingKey,
      }).unwrap();
      setMatchesMergeSuccess(
        `${res.rows_added} row${res.rows_added === 1 ? "" : "s"} added to Matches.`,
      );
      resetMatchesMergeState();
      await loadMatchesFiles(mjManageJobId);
    } catch (err: unknown) {
      setMatchesMergeStep("ready");
      const msg =
        (err as { data?: { message?: string } })?.data?.message ??
        (err instanceof Error ? err.message : "Merge failed.");
      setMatchesMergeError(msg);
    }
  };

  const mjToggleNestedTaskSelect = useCallback((jobId: number, taskId: number, checked: boolean) => {
    setMjNestedTaskSelectionByJob((prev) => {
      const cur = prev[jobId] ?? [];
      const next = checked ? Array.from(new Set([...cur, taskId])) : cur.filter((x) => x !== taskId);
      const out = { ...prev };
      if (next.length) out[jobId] = next;
      else delete out[jobId];
      return out;
    });
  }, []);

  const mjToggleAllNestedTasks = useCallback((jobId: number, taskRows: MjConnectionTask[], checked: boolean) => {
    setMjNestedTaskSelectionByJob((prev) => {
      const out = { ...prev };
      if (!checked || !taskRows.length) delete out[jobId];
      else out[jobId] = taskRows.map((t) => t.id);
      return out;
    });
  }, []);

  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [childEmployeesNum, setChildEmployeesNum] = useState("100");
  const [taskJobListName, setTaskJobListName] = useState("");
  const [childPlans, setChildPlans] = useState<ConnPlanEditorRow[]>([]);
  const [childBotName, setChildBotName] = useState("");
  const [childAdditionalOptions, setChildAdditionalOptions] = useState("");
  const [childProcessErrors, setChildProcessErrors] = useState(false);
  const [childDocumentType, setChildDocumentType] = useState("");
  const [childDocumentTypes, setChildDocumentTypes] = useState<string[]>([" "]);
  const [childUploadAsZip, setChildUploadAsZip] = useState(false);
  const [childUnzipBefore, setChildUnzipBefore] = useState(false);
  const [childUseSsnMatch, setChildUseSsnMatch] = useState(true);
  // path_to_s3_base_folder for upload bots — chosen via folder picker
  const [childSourcePath, setChildSourcePath] = useState("");
  const [childError, setChildError] = useState<string | null>(null);

  const [selectedChildJobIds, setSelectedChildJobIds] = useState<number[]>([]);

  const taskJobRowIdsOnPage = useMemo(
    () =>
      childJobs
        .filter((raw) => !isSSNJob(raw as Record<string, unknown>))
        .map((raw) => Number((raw as Record<string, unknown>).id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [childJobs],
  );

  const allTaskJobRowsSelected =
    taskJobRowIdsOnPage.length > 0 &&
    taskJobRowIdsOnPage.every((id) => selectedChildJobIds.includes(id));
  const taskJobRowsSelectIndeterminate =
    taskJobRowIdsOnPage.some((id) => selectedChildJobIds.includes(id)) && !allTaskJobRowsSelected;

  const toggleSelectAllTaskJobRows = useCallback(
    (checked: boolean) => {
      setSelectedChildJobIds((prev) => {
        if (checked) {
          return Array.from(new Set([...prev, ...taskJobRowIdsOnPage]));
        }
        const drop = new Set(taskJobRowIdsOnPage);
        return prev.filter((id) => !drop.has(id));
      });
    },
    [taskJobRowIdsOnPage],
  );

  // Folder picker dialog — shared between Create and Edit task job dialogs for upload bots
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerTarget, setFolderPickerTarget] = useState<"create" | "edit">("create");
  const [pickerSelectedFolder, setPickerSelectedFolder] = useState<number | null>(null);
  const [pickerSelectedSub, setPickerSelectedSub] = useState<string | null>(null);

  const [majConnCreateOpen, setMajConnCreateOpen] = useState(false);
  const [mcUsername, setMcUsername] = useState("");
  const [mcHost, setMcHost] = useState("");
  const [mcClientCode, setMcClientCode] = useState("");
  const [mcPassword, setMcPassword] = useState("");
  const [mcShowPw, setMcShowPw] = useState(false);
  const [mcError, setMcError] = useState<string | null>(null);

  const [majConnEdit, setMajConnEdit] = useState<AdminVendorConnection | null>(null);
  const [meUsername, setMeUsername] = useState("");
  const [meHost, setMeHost] = useState("");
  const [meClientCode, setMeClientCode] = useState("");
  const [mePassword, setMePassword] = useState("");
  const [meShowPw, setMeShowPw] = useState(false);
  const [meError, setMeError] = useState<string | null>(null);

  const [selectedPoolConnectionIds, setSelectedPoolConnectionIds] = useState<number[]>([]);
  const [deleteConnConfirm, setDeleteConnConfirm] = useState<number | null>(null);
  /** Per-major-job pool: selected connection rows for batch delete. */
  const [poolConnSelectedIds, setPoolConnSelectedIds] = useState<number[]>([]);
  const [poolConnBulkDeleteOpen, setPoolConnBulkDeleteOpen] = useState(false);
  const [poolConnBulkDeleting, setPoolConnBulkDeleting] = useState(false);

  // Active manage-dialog tab. Stable string ids (not positional indices) so the
  // absent SSN tab on delete_bots can't collide with Connections. Persisted in URL.
  const tabFromUrl = useMemo<ManageTab>(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return t === "connections" ? "connections" : t === "ssn" ? "ssn" : "jobs";
  }, [location.search]);
  const [manageTab, setManageTab] = useState<ManageTab>(tabFromUrl);
  useEffect(() => {
    setManageTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleManageTabChange = (_e: React.SyntheticEvent, v: ManageTab) => {
    const params = new URLSearchParams(location.search);
    if (v === "jobs") {
      params.delete("tab");
    } else {
      params.set("tab", v);
    }
    navigate({ search: params.toString() }, { replace: true });
    // Load SSN job data when switching to SSN tab (only for non-delete_bots)
    if (v === "ssn" && activeMajor && activeMajor.bot_kind !== "delete_bots") {
      loadSsnJob(activeMajor.id, { silent: ssnJobData !== null });
    }
  };

  const activeMajor = useMemo(
    () => majors.find((m) => m.id === activeMajorId) ?? null,
    [majors, activeMajorId],
  );

  // Reset to Task jobs tab when switching to a delete_bots major job (no SSN tab there)
  useEffect(() => {
    if (activeMajor?.bot_kind === "delete_bots" && manageTab === "ssn") {
      const params = new URLSearchParams(location.search);
      params.delete("tab");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [activeMajor?.id, activeMajor?.bot_kind, manageTab, location.search, navigate]);

  /** True if at least one child task of this major job is running OR already downloaded >0 employees.
   *  Used to lock destructive edits (vendor / bot kind / job rewiring). */
  const majorHasRunningOrProgress = useMemo(() => {
    if (!activeMajor) return false;
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      if (mjJobAggregateRunning(row)) return true;
      const stats = row.stats as Record<string, unknown> | undefined;
      const processed =
        Number(stats?.success_employees_count ?? 0) +
        Number(stats?.error_employees_count ?? 0);
      if (processed > 0) return true;
    }
    return false;
  }, [activeMajor, childJobs]);

  /** True if any child job currently has at least one running task. */
  const majorHasRunningTasks = useMemo(() => {
    if (!activeMajor) return false;
    return childJobs.some((raw) =>
      mjJobAggregateRunning(raw as Record<string, unknown>),
    );
  }, [activeMajor, childJobs]);

  /** Auto-close the 2FA dialog as soon as the underlying task no longer needs a code.
   *  This handles WS updates: when the bot acknowledges the code, `task2fa_status`
   *  flips away from pending/ready and the dialog disappears immediately. */
  useEffect(() => {
    if (twoFaMjTask == null) return;
    const jobId =
      typeof twoFaMjTask.job === "number" && Number.isFinite(twoFaMjTask.job) ? twoFaMjTask.job : null;
    if (jobId == null) return;
    const tasksInJob = mjAccordionTasksByJob[jobId] ?? [];
    const fresh = tasksInJob.find((t) => t.id === twoFaMjTask.id);
    if (fresh && !shouldShowPass2FAMj(fresh)) {
      setTwoFaMjTask(null);
      setTwoFaMjCode("");
      setTwoFaMjErr(null);
    }
  }, [mjAccordionTasksByJob, twoFaMjTask]);

  const isDetailMode = detailMajorJobId != null;
  const majorsStillLoading = majorsRaw === undefined || majorsFetching;
  const detailMajorNotFound =
    isDetailMode &&
    !majorsStillLoading &&
    detailMajorJobId != null &&
    !majors.some((m) => m.id === detailMajorJobId);

  /** All bot connections for this user that match the active major's vendor (pool is kept in sync on the server). */
  const poolConnIdsForMajor = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    return connections
      .filter((c) => normVendor(c.vendor) === want)
      .map((c) => c.id)
      .sort((a, b) => a - b);
  }, [activeMajor, connections]);

  /**
   * Logins explicitly attached to this automation (`MajorJobConnection` / included_connection_ids), vendor-filtered.
   * Task job PATCH only accepts plan rows whose connection_id is listed here unless we merge first — see save flows.
   */
  const majorIncludedConnIds = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    const vendorIds = new Set(
      connections.filter((c) => normVendor(c.vendor) === want).map((c) => c.id),
    );
    return Array.from(new Set((activeMajor.included_connection_ids ?? []).filter((id) => id >= 1)))
      .filter((id) => vendorIds.has(id))
      .sort((a, b) => a - b);
  }, [activeMajor, connections]);

  /**
   * Dropdown pool for allocating tasks to logins: every vendor-matching login for this user is
   * eligible. Picking one that isn't yet attached to the major job triggers
   * ``ensureMajorIncludesConnectionIds`` on save, which adds it to the pool.
   *
   * Linked rows from ``MajorJobConnection`` come first so operators see the "primary" ones at
   * the top of the dropdown, and we append the rest sorted by id.
   */
  const majorConnPickerIds = useMemo(() => {
    if (!activeMajor) return [];
    const linked = majorIncludedConnIds;
    const rest = poolConnIdsForMajor.filter((cid) => !linked.includes(cid));
    return [...linked, ...rest];
  }, [activeMajor, majorIncludedConnIds, poolConnIdsForMajor]);

  const nextUnusedChildPlanConnId = useMemo(
    () => nextUnusedPoolConnectionId(majorConnPickerIds, childPlans),
    [majorConnPickerIds, childPlans],
  );

  const ensureMajorIncludesConnectionIds = useCallback(
    async (connectionIds: number[]) => {
      if (!canEditBots) return;
      if (!activeMajor) return;
      const uniq = Array.from(new Set(connectionIds.filter((id) => id >= 1))).sort((a, b) => a - b);
      if (!uniq.length) return;
      const cur = activeMajor.included_connection_ids ?? [];
      const missing = uniq.filter((id) => !cur.includes(id));
      if (!missing.length) return;
      const merged = Array.from(new Set([...cur, ...uniq])).sort((a, b) => a - b);
      await updateMajorJob({
        majorJobId: activeMajor.id,
        invalidateUserId: userId,
        body: { included_connection_ids: merged },
      }).unwrap();
      await refetch();
    },
    [canEditBots, activeMajor, updateMajorJob, userId, refetch],
  );

  /** Stale IDs still stored on the major (e.g. before the next PATCH) or wrong-vendor rows. */
  const poolOrphans = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    const vendorMatchIds = new Set(
      connections.filter((c) => normVendor(c.vendor) === want).map((c) => c.id),
    );
    return (activeMajor.included_connection_ids ?? []).filter((cid) => !vendorMatchIds.has(cid));
  }, [activeMajor, connections]);

  /** 1-based index of the task job in this major job's list (API order); not Django task job id. */
  const mjEditJobOrdinal = useMemo(() => {
    if (mjEditJobId == null) return null;
    const idx = childJobs.findIndex((raw) => Number((raw as Record<string, unknown>).id) === mjEditJobId);
    return idx >= 0 ? idx + 1 : null;
  }, [mjEditJobId, childJobs]);

  /** 1-based index of a deleted job target (for confirmation copy). */
  const mjDeleteJobOrdinal = useMemo(() => {
    if (mjDeleteJobId == null) return null;
    const idx = childJobs.findIndex(
      (raw) => Number((raw as Record<string, unknown>).id) === mjDeleteJobId,
    );
    return idx >= 0 ? idx + 1 : null;
  }, [mjDeleteJobId, childJobs]);

  /** 1-based index of a task within its parent job's task list. */
  const taskOrdinalInJob = useCallback(
    (jobId: number, taskId: number) => {
      const tasks = mjAccordionTasksByJob[jobId] ?? [];
      const idx = tasks.findIndex((t) => t.id === taskId);
      return idx >= 0 ? idx + 1 : null;
    },
    [mjAccordionTasksByJob],
  );

  const { data: childRegistryBots } = useGetBotRegistryQuery(
    {
      vendor: activeMajor?.vendor ?? undefined,
      jobKind: activeMajor?.bot_kind ?? undefined,
      isActive: true,
      pageSize: 120,
    },
    { skip: !activeMajor?.vendor || (!childDialogOpen && !mjEditJobOpen) },
  );

  // Storage folders for the selected user — used by the upload-bot source folder picker.
  const { data: storageFoldersData } = useGetBackendUserStorageFoldersQuery(userId, {
    skip: !folderPickerOpen,
  });
  const storageFolders = storageFoldersData?.folders ?? [];
  const normalizedChildBots = useMemo(
    () => normalizeBotRegistryPayload(childRegistryBots),
    [childRegistryBots],
  );
  const mjEffectiveManagedJob = useMemo((): Record<string, unknown> | null => {
    if (mjManageJobId == null) return null;
    if (
      mjManageJobDetail != null &&
      Number((mjManageJobDetail as Record<string, unknown>).id) === mjManageJobId
    ) {
      return mjManageJobDetail as Record<string, unknown>;
    }
    const fromList = childJobs.find((raw) => Number((raw as Record<string, unknown>).id) === mjManageJobId);
    return (fromList as Record<string, unknown> | undefined) ?? null;
  }, [mjManageJobId, mjManageJobDetail, childJobs]);

  /** Full S3 path built from the current picker selection. */
  const pickerBuiltPath = useMemo(() => {
    if (!pickerSelectedFolder) return "";
    const folder = storageFolders.find((f) => f.id === pickerSelectedFolder);
    if (!folder) return "";
    const prefix = (folder.folder_prefix ?? "").replace(/\/$/, "");
    return pickerSelectedSub ? `${prefix}/${pickerSelectedSub}` : prefix;
  }, [pickerSelectedFolder, pickerSelectedSub, storageFolders]);

  const openFolderPicker = (target: "create" | "edit", currentPath?: string) => {
    setFolderPickerTarget(target);
    setPickerSelectedSub(null);
    // Pre-select folder matching the current path
    if (currentPath) {
      const matched = storageFolders.find(
        (f) => currentPath.startsWith((f.folder_prefix ?? "").replace(/\/$/, "")),
      );
      setPickerSelectedFolder(matched?.id ?? null);
      if (matched) {
        const rest = currentPath.slice((matched.folder_prefix ?? "").replace(/\/$/, "").length).replace(/^\//, "");
        const sub = rest.split("/")[0];
        if (sub && matched.subfolders.includes(sub)) setPickerSelectedSub(sub);
      }
    } else {
      setPickerSelectedFolder(null);
    }
    setFolderPickerOpen(true);
  };

  const confirmFolderPicker = () => {
    if (!pickerBuiltPath) return;
    if (folderPickerTarget === "create") {
      setChildSourcePath(pickerBuiltPath);
    } else {
      setMjEditSourcePath(pickerBuiltPath);
    }
    setFolderPickerOpen(false);
  };

  /** 1-based index of the currently managed job in this major's list (UI-only label). */
  const mjEffectiveManagedJobOrdinal = useMemo(() => {
    if (!mjEffectiveManagedJob) return null;
    const idx = childJobs.findIndex(
      (raw) => Number((raw as Record<string, unknown>).id) === Number(mjEffectiveManagedJob.id),
    );
    return idx >= 0 ? idx + 1 : null;
  }, [mjEffectiveManagedJob, childJobs]);

  /** True when the currently-being-edited task job has any task running OR any
   *  downloaded employees. Used to lock destructive edits on the job. */
  const mjEditJobHasRunningOrProgress = useMemo(() => {
    if (mjEditJobId == null) return false;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    if (!raw) return false;
    if (mjJobAggregateRunning(raw)) return true;
    const stats = raw.stats as Record<string, unknown> | undefined;
    const processed =
      Number(stats?.success_employees_count ?? 0) + Number(stats?.error_employees_count ?? 0);
    return processed > 0;
  }, [mjEditJobId, childJobs]);

  const mjEditJobHasRunningTasks = useMemo(() => {
    if (mjEditJobId == null) return false;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    return Boolean(raw && mjJobAggregateRunning(raw));
  }, [mjEditJobId, childJobs]);

  /** Employees already processed (success + error) - any progress locks bot type. */
  const mjEditJobProcessedEmployees = useMemo(() => {
    if (mjEditJobId == null) return 0;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    const stats = raw?.stats as Record<string, unknown> | undefined;
    const fromTotal = Number(stats?.processed_employees_count);
    if (Number.isFinite(fromTotal)) return Math.max(0, fromTotal);
    const suc = Number(stats?.success_employees_count ?? 0) || 0;
    const err = Number(stats?.error_employees_count ?? 0) || 0;
    return suc + err;
  }, [mjEditJobId, childJobs]);

  const mjEditJobBotLocked = mjEditJobProcessedEmployees > 0;

  const shouldShowPass2FAMj = (task: MjConnectionTask) => {
    const task2fa = (task.task2fa_status || "").toLowerCase();
    return task.bot_work_status === "2FA" && ["pending", "ready"].includes(task2fa);
  };

  const reloadMjAccordionTasks = useCallback(
    async (jobId: number) => {
      try {
        const raw = await dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
        ).unwrap();
        setMjJobDetailById((prev) => ({
          ...prev,
          [jobId]: raw as Record<string, unknown>,
        }));
        const warnRaw = (
          raw as { stats?: { range_coverage?: { warning?: string } } }
        ).stats?.range_coverage?.warning;
        const warn = mjArtifactRangeWarning(typeof warnRaw === "string" ? warnRaw : null);
        setMjJobRangeWarningsByJobId((prev) => {
          const n = { ...prev };
          if (warn) n[jobId] = warn;
          else delete n[jobId];
          return n;
        });
        const tasksUnknown = raw.tasks as unknown;
        const list = Array.isArray(tasksUnknown)
          ? (tasksUnknown as Record<string, unknown>[])
          : [];
        const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));
        setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
        setMjAccordionCacheTs((prev) => ({ ...prev, [jobId]: Date.now() }));
        return mapped;
      } catch {
        return null;
      }
    },
    [dispatch],
  );

  const ACCORDION_CACHE_TTL_MS = 30_000; // 30 seconds

  // Clear prefetch tracking when switching to a different major job
  useEffect(() => {
    prefetchedJobIdsRef.current.clear();
  }, [activeMajorId, detailMajorJobId]);

  // Background-prefetch task details for every child job as soon as the list arrives.
  // This way accordion data is ready before the user even clicks.
  useEffect(() => {
    if (!childJobs.length) return;

    const newIds = childJobs
      .map((raw) => Number((raw as Record<string, unknown>).id))
      .filter((id) => {
        if (!id || prefetchedJobIdsRef.current.has(id)) return false;
        // Skip jobs whose data is still fresh in the cache
        const cachedAt = mjAccordionCacheTs[id] ?? 0;
        if (mjAccordionTasksByJobRef.current[id] && Date.now() - cachedAt < ACCORDION_CACHE_TTL_MS) return false;
        return true;
      });

    if (!newIds.length) return;
    newIds.forEach((id) => prefetchedJobIdsRef.current.add(id));

    // Small delay so the list renders first, then fire all requests concurrently
    const timer = setTimeout(() => {
      newIds.forEach((jobId) => {
        dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false }),
        ).then((result) => {
          if (!("data" in result) || !result.data) return;
          const detail = result.data as Record<string, unknown>;
          const tasksUnknown = detail.tasks as unknown;
          const list = Array.isArray(tasksUnknown) ? (tasksUnknown as Record<string, unknown>[]) : [];
          const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));

          setMjJobDetailById((prev) => ({ ...prev, [jobId]: detail }));
          setMjAccordionTasksByJob((prev) => {
            // Don't overwrite fresher data that arrived via WS or manual refresh
            const existing = prev[jobId];
            if (existing && (mjAccordionCacheTs[jobId] ?? 0) > Date.now() - 1000) return prev;
            return { ...prev, [jobId]: mapped };
          });
          setMjAccordionCacheTs((prev) => ({ ...prev, [jobId]: Date.now() }));

          const warnRaw = (detail as { stats?: { range_coverage?: { warning?: string } } })
            .stats?.range_coverage?.warning;
          const warn = mjArtifactRangeWarning(typeof warnRaw === "string" ? warnRaw : null);
          setMjJobRangeWarningsByJobId((prev) => {
            const n = { ...prev };
            if (warn) n[jobId] = warn;
            else delete n[jobId];
            return n;
          });
        });
      });
    }, 150);

    return () => clearTimeout(timer);
  }, [childJobs, dispatch]); // mjAccordionCacheTs intentionally omitted — only used as read-only snapshot

  const handleMjAccordionExpanded = (_event: SyntheticEvent, jobId: number, expanded: boolean) => {
    setExpandedMjJobAccordion((prev) => ({ ...prev, [jobId]: expanded }));
    if (!expanded) return;

    const cachedAt = mjAccordionCacheTs[jobId] ?? 0;
    const hasData = Boolean(mjAccordionTasksByJob[jobId]);
    const isFresh = hasData && Date.now() - cachedAt < ACCORDION_CACHE_TTL_MS;

    // Fresh cache — render instantly, nothing to fetch.
    if (isFresh) return;
    // A reload is already running for this job — don't stack another (rapid open/close/open).
    if (mjAccordionInFlightRef.current.has(jobId)) return;

    // Block with the spinner ONLY when there is nothing to show yet. When stale
    // data exists we render it immediately and refresh silently in the background.
    if (!hasData) setMjAccordionLoadingJobId(jobId);

    mjAccordionInFlightRef.current.add(jobId);
    void reloadMjAccordionTasks(jobId).finally(() => {
      mjAccordionInFlightRef.current.delete(jobId);
      setMjAccordionLoadingJobId((cur) => (cur === jobId ? null : cur));
    });
  };

  const refreshMjJobDetail = async (jobId: number, refreshList = false) => {
    setMjRefreshingJobIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      const details = await dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      setMjJobDetailById((prev) => ({
        ...prev,
        [jobId]: details as Record<string, unknown>,
      }));
      const statsRaw = (
        details as { stats?: { range_coverage?: { warning?: string } } }
      ).stats?.range_coverage?.warning;
      const stats = mjArtifactRangeWarning(typeof statsRaw === "string" ? statsRaw : null);
      setMjJobRangeWarningsByJobId((prev) => {
        const n = { ...prev };
        if (stats) n[jobId] = stats;
        else delete n[jobId];
        return n;
      });
      if (mjManageJobId === jobId) setMjManageJobDetail(details as Record<string, unknown>);
      const tasksUnknown = (details as { tasks?: unknown }).tasks;
      const list = Array.isArray(tasksUnknown) ? (tasksUnknown as Record<string, unknown>[]) : [];
      const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));
      setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
      if (refreshList) await safeRefetchChildJobs();
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Failed to refresh task job."),
      }));
    } finally {
      setMjRefreshingJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  const refreshMjJobRow = async (jobId: number) => {
    await refreshMjJobDetail(jobId, true);
  };

  useEffect(() => {
    expandedMjJobAccordionRef.current = expandedMjJobAccordion;
  }, [expandedMjJobAccordion]);

  useEffect(() => {
    mjAccordionTasksByJobRef.current = mjAccordionTasksByJob;
  }, [mjAccordionTasksByJob]);

  useEffect(() => {
    reloadMjAccordionTasksRef.current = reloadMjAccordionTasks;
  }, [reloadMjAccordionTasks]);

  useEffect(() => {
    refreshMjJobDetailRef.current = refreshMjJobDetail;
  });

  useEffect(() => { ssnJobDataRef.current = ssnJobData; }, [ssnJobData]);
  useEffect(() => { ssnMajorIdRef.current = activeMajorId ?? detailMajorJobId ?? null; }, [activeMajorId, detailMajorJobId]);
  useEffect(() => { loadSsnJobRef.current = loadSsnJob; });

  useEffect(() => {
    const scheduleMjWsAction = (key: string, action: () => void, delayMs: number) => {
      const timers = mjWsDebounceTimersRef.current;
      if (timers[key]) clearTimeout(timers[key]);
      timers[key] = setTimeout(() => {
        delete timers[key];
        action();
      }, delayMs);
    };

    const reloadExpandedJobTasksForTask = (taskId: number) => {
      let matchedJobId: number | null = null;
      for (const [jobIdRaw, tasks] of Object.entries(mjAccordionTasksByJobRef.current)) {
        if (tasks.some((task) => task.id === taskId)) {
          matchedJobId = Number(jobIdRaw);
          break;
        }
      }
      if (matchedJobId != null && expandedMjJobAccordionRef.current[matchedJobId]) {
        scheduleMjWsAction(
          `task-job-${matchedJobId}`,
          () => void reloadMjAccordionTasksRef.current(matchedJobId as number),
          400,
        );
        return;
      }
      for (const [jobIdRaw, expanded] of Object.entries(expandedMjJobAccordionRef.current)) {
        if (!expanded) continue;
        const jobId = Number(jobIdRaw);
        scheduleMjWsAction(`task-job-${jobId}`, () => void reloadMjAccordionTasksRef.current(jobId), 400);
      }
    };

    const reloadSsnIfMatch = (matchJobId: number | null, matchTaskId: number | null) => {
      const ssnData = ssnJobDataRef.current;
      const majorId = ssnMajorIdRef.current;
      if (!ssnData || majorId == null) return;
      const ssnJobId = ssnData.job != null ? Number((ssnData.job as Record<string, unknown>).id ?? 0) : null;
      const ssnTaskId = ssnData.task != null ? Number((ssnData.task as Record<string, unknown>).id ?? 0) : null;
      const matches =
        (matchJobId != null && ssnJobId != null && matchJobId === ssnJobId) ||
        (matchTaskId != null && ssnTaskId != null && matchTaskId === ssnTaskId);
      if (matches) {
        scheduleMjWsAction(`ssn-job-reload-${majorId}`, () => void loadSsnJobRef.current(majorId, { silent: true }), 600);
      }
    };

    const unsubscribeEvents = botsManagementWs.subscribe((payload: BotsManagementWsEvent) => {
      const jobId = payload.job?.job_id;
      if (payload.event === "job.updated" && jobId != null) {
        reloadSsnIfMatch(Number(jobId), null);
        scheduleMjWsAction(`job-stats-${jobId}`, () => {
          void refreshMjJobDetailRef.current(Number(jobId), false);
        }, 800);
        return;
      }

      const taskId = payload.task?.task_id;
      if (taskId == null) return;

      const eventName = payload.event ?? "task.updated";
      if (eventName === "task2fa.updated" || eventName === "task.updated") {
        reloadSsnIfMatch(null, Number(taskId));
        reloadExpandedJobTasksForTask(Number(taskId));
      }
    });

    const unsubscribeStatus = botsManagementWs.subscribeStatus((connected) => {
      setMjWsConnected(connected);
      if (!connected) return;
      scheduleMjWsAction("ws-reconnect", () => {
        for (const [jobIdRaw, expanded] of Object.entries(expandedMjJobAccordionRef.current)) {
          if (expanded) void reloadMjAccordionTasksRef.current(Number(jobIdRaw));
        }
      }, 1000);
    });

    return () => {
      unsubscribeEvents();
      unsubscribeStatus();
      Object.values(mjWsDebounceTimersRef.current).forEach((timer) => clearTimeout(timer));
      mjWsDebounceTimersRef.current = {};
    };
  }, []);

  /** Slow fallback only when WebSocket is disconnected (e.g. no Redis). */
  useEffect(() => {
    if (mjWsConnected) return undefined;
    const expandedIds = Object.entries(expandedMjJobAccordion)
      .filter(([, expanded]) => expanded)
      .map(([jobId]) => Number(jobId));
    if (!expandedIds.length) return undefined;
    const interval = window.setInterval(() => {
      expandedIds.forEach((jobId) => void reloadMjAccordionTasks(jobId));
    }, 30000);
    return () => window.clearInterval(interval);
  }, [mjWsConnected, expandedMjJobAccordion, reloadMjAccordionTasks]);

  /**
   * Resilience poll: the WS can be "connected but mute" (stale Daphne / no Redis),
   * which would otherwise hide transient states like 2FA. While an expanded job has
   * an active task, refresh it every few seconds regardless of WS health.
   */
  useEffect(() => {
    const expandedIds = Object.entries(expandedMjJobAccordion)
      .filter(([, expanded]) => expanded)
      .map(([jobId]) => Number(jobId));
    if (!expandedIds.length) return undefined;
    // Poll while any task is not in a terminal state — including PENDING. Otherwise a
    // PENDING → PROCESSING → 2FA transition is never picked up when the WS is mute
    // (connected-but-no-Redis on Dev), so the 2FA button would only surface on a
    // manual refresh.
    const TERMINAL_STATUSES = ["FINISHED", "STOPPED", "ERROR", "CRITICAL_ERROR"];
    const hasActiveTask = (jobId: number) =>
      (mjAccordionTasksByJobRef.current[jobId] ?? []).some(
        (t) => !TERMINAL_STATUSES.includes((t.task_status || "").toUpperCase()),
      );
    const interval = window.setInterval(() => {
      expandedIds.forEach((jobId) => {
        if (hasActiveTask(jobId)) void reloadMjAccordionTasks(jobId);
      });
    }, 8000);
    return () => window.clearInterval(interval);
  }, [expandedMjJobAccordion, reloadMjAccordionTasks]);

  const openMjManage = (jobId: number) => {
    setMjManageJobError(null);
    setMjManageJobId(jobId);
    // Reset Matches tab state
    setMjManageTab(0);
    setMatchesFiles(null);
    setMatchesError(null);
    setMatchesSelectedFiles([]);
    setMatchesMergeSuccess(null);
    resetMatchesMergeState();
    const snap = childJobs.find((raw) => Number((raw as Record<string, unknown>).id) === jobId);
    setMjManageJobDetail(snap ? (snap as Record<string, unknown>) : null);
    void reloadMjAccordionTasks(jobId);
    void dispatch(
      adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
    )
      .unwrap()
      .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
      .catch(() => {});
  };

  const openMjAddTasksDialog = async () => {
    if (mjManageJobId == null) return;
    const jid = mjManageJobId;
    let snap: Record<string, unknown> | null = null;
    setMjAddTasksLoading(true);
    try {
      const raw = await dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      setMjManageJobDetail(raw as Record<string, unknown>);
      snap = raw as Record<string, unknown>;
    } catch {
      snap =
        (mjManageJobDetail != null && Number((mjManageJobDetail as Record<string, unknown>).id) === jid
          ? (mjManageJobDetail as Record<string, unknown>)
          : null) ??
        ((childJobs.find((r) => Number((r as Record<string, unknown>).id) === jid) ?? null) as Record<
          string,
          unknown
        > | null);
    }

    if (snap != null) {
      const plans = parseConnectionPlansFromJobRow(snap);
      if (plans.length) {
        const pool = Array.from(new Set(plans.map((p) => p.connection_id))).sort((a, b) => a - b);
        setMjAddPlanConnPool(pool);
        const head = pool[0];
        setMjBulkAddRows([{ connection_id: head != null ? head : "", task_target_count: "1" }]);
      } else {
        setMjAddPlanConnPool([]);
        const lc =
          legacyPrimaryConnectionFromJob(snap) ?? (majorConnPickerIds[0] != null ? majorConnPickerIds[0] : "");
        const cid = typeof lc === "number" && lc >= 1 ? lc : "";
        setMjBulkAddRows([{ connection_id: cid, task_target_count: "1" }]);
      }
    } else {
      setMjAddPlanConnPool([]);
      const fb = majorConnPickerIds[0];
      setMjBulkAddRows([{ connection_id: fb != null ? fb : "", task_target_count: "1" }]);
    }

    setMjAddTasksJobId(jid);
    setMjAddTasksErr(null);
    void reloadMjAccordionTasks(jid);
    setMjAddTasksOpen(true);
    setMjAddTasksLoading(false);
  };

  const handleMjAddTasksConfirmed = async () => {
    if (!canEditBots) return;
    if (mjAddTasksJobId == null) return;

    let currentTasks = mjAccordionTasksByJob[mjAddTasksJobId] ?? [];

    const manageDetailSnap =
      mjManageJobDetail != null && Number(mjManageJobDetail.id) === mjAddTasksJobId
        ? mjManageJobDetail
        : null;

    const rawTasksOnDetail = manageDetailSnap
      ? ((manageDetailSnap as Record<string, unknown>).tasks as unknown)
      : undefined;
    const arrFromDetail =
      rawTasksOnDetail &&
      Array.isArray(rawTasksOnDetail) &&
      (currentTasks.length === 0 || (rawTasksOnDetail as unknown[]).length >= currentTasks.length)
        ? (rawTasksOnDetail as Record<string, unknown>[])
        : null;

    if (arrFromDetail && arrFromDetail.length > 0) {
      currentTasks = arrFromDetail.map((t) => normalizeRawTaskToMj(t, mjAddTasksJobId));
    }

    const rawJobSnapshot =
      (mjEffectiveManagedJob != null && Number(mjEffectiveManagedJob.id) === mjAddTasksJobId
        ? mjEffectiveManagedJob
        : null) ??
      (manageDetailSnap as Record<string, unknown> | null) ??
      ((childJobs.find((r) => Number((r as Record<string, unknown>).id) === mjAddTasksJobId) ??
        null) as Record<string, unknown> | null);

    const plansSnap = rawJobSnapshot ? parseConnectionPlansFromJobRow(rawJobSnapshot) : [];
    const planMode = plansSnap.length > 0;
    const jobTotalRemain = Math.max(0, MAX_BATCH_TASKS - currentTasks.length);

    const resolved = resolveMjBulkTaskAddPayload(
      planMode,
      plansSnap,
      mjAddPlanConnPool,
      mjBulkAddRows,
      currentTasks,
      jobTotalRemain,
      connections,
    );

    if ("error" in resolved) {
      setMjAddTasksErr(resolved.error);
      return;
    }

    setMjAddTasksErr(null);
    const jid = mjAddTasksJobId;
    try {
      if (resolved.planMode) {
        for (const batch of resolved.batches) {
          await addTasksToJobMutation({
            jobId: jid,
            count: batch.count,
            connection_id: batch.connection_id,
          }).unwrap();
        }
      } else {
        await addTasksToJobMutation({
          jobId: jid,
          count: resolved.legacyCount,
        }).unwrap();
      }

      if (mjAddPlanConnPool.length) {
        const h = mjAddPlanConnPool[0];
        setMjBulkAddRows([{ connection_id: h!, task_target_count: "1" }]);
      } else {
        setMjBulkAddRows((prev) => [{ connection_id: prev[0]?.connection_id ?? "", task_target_count: "1" }]);
      }

      setMjAddTasksOpen(false);
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[jid]) await reloadMjAccordionTasks(jid);
      void dispatch(adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }))
        .unwrap()
        .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
        .catch(() => {});
    } catch (e) {
      setMjAddTasksErr(getErr(e, "Could not add tasks."));
    }
  };

  const mjRunTaskRow = async (jobId: number, task: MjConnectionTask) => {
    if (!canEditBots) return;
    setBusyTaskId(task.id);
    try {
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      if (String(task.task_status ?? "") !== "PENDING") {
        await setTaskPendingMutation(task.id).unwrap();
      }
      await runTaskMutation(task.id).unwrap();
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not run task."),
      }));
    } finally {
      setBusyTaskId((cur) => (cur === task.id ? null : cur));
    }
  };

  const mjStopTaskRow = async (jobId: number, taskId: number) => {
    if (!canEditBots) return;
    setBusyTaskId(taskId);
    try {
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      await stopTaskMutation(taskId).unwrap();
      if (twoFaMjTask?.id === taskId) {
        setTwoFaMjTask(null);
        setTwoFaMjCode("");
        setTwoFaMjErr(null);
      }
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not stop task."),
      }));
    } finally {
      setBusyTaskId((cur) => (cur === taskId ? null : cur));
    }
  };

  const mjRunJob = async () => {
    if (!canEditBots) return;
    if (mjManageJobId == null) return;
    setMjManageJobError(null);
    try {
      await runTasksBatchMut({ job_id: mjManageJobId }).unwrap();
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[mjManageJobId]) await reloadMjAccordionTasks(mjManageJobId);
      void dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(mjManageJobId, { subscribe: false, forceRefetch: true }),
      )
        .unwrap()
        .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
        .catch(() => {});
    } catch (e) {
      setMjManageJobError(getErr(e, "Failed to run job."));
    }
  };

  const mjStopJob = async () => {
    if (!canEditBots) return;
    if (mjManageJobId == null) return;
    setMjManageJobError(null);
    try {
      await stopTasksBatchMut({ job_id: mjManageJobId }).unwrap();
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[mjManageJobId]) await reloadMjAccordionTasks(mjManageJobId);
      void dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(mjManageJobId, { subscribe: false, forceRefetch: true }),
      )
        .unwrap()
        .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
        .catch(() => {});
    } catch (e) {
      setMjManageJobError(getErr(e, "Failed to stop job."));
    }
  };

  const handleMjSubmit2FACode = async () => {
    if (!canEditBots) return;
    if (twoFaMjTask == null) return;
    const submittedTaskId = twoFaMjTask.id;
    const trimmed = twoFaMjCode.trim();
    if (!trimmed) {
      setTwoFaMjErr("2FA code is required.");
      return;
    }
    let jobIdMaybe: number | null = null;
    if (typeof twoFaMjTask.job === "number" && Number.isFinite(twoFaMjTask.job) && twoFaMjTask.job > 0) {
      jobIdMaybe = twoFaMjTask.job;
    }
    setTwoFaMjErr(null);
    try {
      await submitTask2FACodeMut({ taskId: twoFaMjTask.id, code: trimmed }).unwrap();
      setTwoFaMjTask(null);
      setTwoFaMjCode("");
      await safeRefetchChildJobs();
      if (jobIdMaybe != null && expandedMjJobAccordion[jobIdMaybe]) {
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const mapped = await reloadMjAccordionTasks(jobIdMaybe);
          const currentTask = mapped?.find((task) => task.id === submittedTaskId);
          if (!currentTask || !shouldShowPass2FAMj(currentTask)) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 700));
        }
      }
    } catch (e) {
      setTwoFaMjErr(getErr(e, "Could not submit 2FA code."));
    }
  };

  const mjConfirmDeleteNestedTask = async () => {
    if (!canEditBots) return;
    const tid = mjDeleteSingleTaskId;
    if (tid == null) return;
    const matched = Object.entries(mjAccordionTasksByJob).find(([, ta]) => ta.some((t) => t.id === tid));
    const aid = matched ? Number(matched[0]) : undefined;
    try {
      await deleteTaskMutation(tid).unwrap();
      setMjDeleteSingleTaskId(null);
      if (aid != null && aid > 0) {
        setMjNestedTaskSelectionByJob((prev) => {
          const cur = prev[aid];
          if (!cur?.length) return prev;
          const next = cur.filter((x) => x !== tid);
          const out = { ...prev };
          if (next.length) out[aid] = next;
          else delete out[aid];
          return out;
        });
      }
      if (twoFaMjTask?.id === tid) {
        setTwoFaMjTask(null);
        setTwoFaMjCode("");
        setTwoFaMjErr(null);
      }
      await safeRefetchChildJobs();
      if (aid != null && aid > 0) await reloadMjAccordionTasks(aid);
    } catch (e) {
      if (aid != null && aid > 0) {
        setMjJobActionAlertsByJobId((prev) => ({
          ...prev,
          [aid]: getErr(e, "Could not delete task."),
        }));
      }
    }
  };

  const mjConfirmBatchDeleteNestedTasks = async () => {
    if (!canEditBots) return;
    const pack = mjBatchDeleteNestDialog;
    if (pack == null || !pack.ids.length) return;
    const jobId = pack.jobId;
    try {
      await tasksBatchDeleteNestMut({ task_ids: pack.ids }).unwrap();
      setMjBatchDeleteNestDialog(null);
      setMjNestedTaskSelectionByJob((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[jobId]) await reloadMjAccordionTasks(jobId);
      if (mjManageJobId === jobId) {
        void dispatch(adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }))
          .unwrap()
          .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
          .catch(() => {});
      }
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not delete tasks."),
      }));
    }
  };
  const mjConfirmDeleteJob = async () => {
    if (!canEditBots) return;
    const delId = mjDeleteJobId;
    if (delId == null) return;
    setMjManageJobError(null);
    try {
      await deleteTaskJobMut({ jobId: delId, deleteLogs: mjDeleteJobLogs }).unwrap();
      setMjDeleteJobId(null);
      setMjDeleteJobLogs(false);
      setMjManageJobId(null);
      setMjManageJobDetail(null);
      setMjAccordionTasksByJob((prev) => {
        const next = { ...prev };
        delete next[delId];
        return next;
      });
      await safeRefetchChildJobs();
      await refetch();
    } catch (e) {
      setMjManageJobError(getErr(e, "Failed to delete job."));
    }
  };

  const handleMjViewJobFiles = (job: Record<string, unknown>) => {
    const prefix = String(job.data_storage_prefix ?? job.path_to_s3_base_folder ?? "").trim();
    const rootPrefix = String(job.data_storage_root_prefix ?? "").trim();
    const rootDisplay = String(job.data_storage_root_display_name ?? "").trim();
    const segs = prefix.split("/").filter(Boolean);
    let leaf = segs.length ? (segs[segs.length - 1] ?? "") : "";
    if (!leaf || leaf === "data") {
      leaf = extractDefaultFolderSlugFromBotName(String(job.bot_name ?? ""));
    }
    const folderMappingSeed =
      rootPrefix && rootDisplay
        ? buildDataStorageFolderMappingSeed(rootDisplay, rootPrefix, prefix)
        : undefined;
    navigate("/data-storage", {
      state: {
        initialPrefix: prefix,
        initialFname: leaf || mjJobDisplayName(job),
        ...(folderMappingSeed ? { folderMappingSeed } : {}),
        fromBotsManagement: true,
      },
    });
  };

  const handleMjDownloadJobLogs = async (jobId: number, job?: Record<string, unknown>) => {
    setMjManageJobError(null);
    try {
      const blob = await triggerJobLogsArchive(jobId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sanitize = (s: string) =>
        s.replace(/^@/, "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
      const client = sanitize((userLabel ?? "").trim() || `user-${userId}`);
      const jobName = sanitize(job ? mjJobDisplayName(job) : `job-${jobId}`);
      a.download = `${client}_${jobName}.zip` || `job-${jobId}-logs.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMjManageJobError(getErr(e, "No logs found for this job yet."));
    }
  };

  const handleMjClearJobLogs = async () => {
    if (!canEditBots) return;
    if (mjClearLogsJobId == null) return;
    const jobId = mjClearLogsJobId;
    setMjManageJobError(null);
    try {
      await clearJobLogsMut(jobId).unwrap();
      setMjClearLogsJobId(null);
      if (expandedMjJobAccordionRef.current[jobId]) {
        void reloadMjAccordionTasksRef.current(jobId);
      }
      // Stats are reset server-side together with the logs — refresh the rows.
      await safeRefetchChildJobs();
    } catch (e) {
      setMjClearLogsJobId(null);
      setMjManageJobError(getErr(e, "Failed to clear logs."));
    }
  };

  const openMjEditJobDialogFor = (j: Record<string, unknown> | null) => {
    if (j == null || activeMajor == null) return;
    const jobId = Number(j.id);
    if (!jobId) return;
    setMjEditJobError(null);
    setMjEditJobId(jobId);
    setMjEditBaselineFrom(Number(j.employee_number_from ?? 0));
    setMjEditJobName(mjJobDisplayName(j));
    setMjEditBotName(String(j.bot_name ?? ""));
    setMjEditEmployeeTo(String(j.employee_number_to ?? "0"));
    setMjEditDocumentType(String(j.document_type ?? "") || "");
    const rawAddOpts = String(j.additional_options ?? "") || "";
    if (activeMajor.bot_kind === "delete_bots") {
      const { types, remaining } = extractDocsTypeFromOptions(rawAddOpts);
      setMjEditDocumentTypes(types);
      setMjEditAdditionalOptions(remaining);
      setMjEditProcessErrors(false);
    } else {
      const { processErrors, remaining } = extractSkipErrorEmployees(rawAddOpts);
      setMjEditAdditionalOptions(remaining);
      setMjEditProcessErrors(processErrors);
    }
    setMjEditUploadAsZip(Boolean(j.upload_as_zip));
    setMjEditUnzipBefore(Boolean(j.unzip_folder_before_uploading));
    setMjEditUseSsnMatch(j.use_ssn_match !== false);
    setMjEditSourcePath(String(j.path_to_s3_base_folder ?? ""));
    setMjEditJobOpen(true);
  };

  const openMjEditJobDialog = () => openMjEditJobDialogFor(mjEffectiveManagedJob);

  const saveMjEditJob = async () => {
    if (!canEditBots) return;
    if (mjEditJobId == null || activeMajor == null) return;
    setMjEditJobError(null);
    const employeeTo = parseEmployeesNumber(mjEditEmployeeTo);
    if (employeeTo == null) {
      setMjEditJobError("Employees to must be a positive integer.");
      return;
    }
    if (employeeTo < mjEditBaselineFrom) {
      setMjEditJobError("Employee To must be >= Employee From.");
      return;
    }
    const snapshotId = mjEditJobId;
    const updatedBot = mjEditBotName.trim();
    if (!updatedBot) {
      setMjEditJobError("Bot is required.");
      return;
    }
    const isUploadEdit = activeMajor.bot_kind === "upload_bots";
    const isDeleteEdit = activeMajor.bot_kind === "delete_bots";
    if (isUploadEdit && !mjEditDocumentType.trim()) {
      setMjEditJobError("Document type is required.");
      return;
    }
    if (isDeleteEdit) {
      const validTypes = mjEditDocumentTypes.filter((t) => t.trim());
      if (!validTypes.length) {
        setMjEditJobError("At least one document type is required.");
        return;
      }
    }
    const editAddOpts = isDeleteEdit
      ? buildDeleteAdditionalOptions(mjEditAdditionalOptions.trim(), mjEditDocumentTypes) || null
      : applySkipErrorEmployees(mjEditAdditionalOptions.trim(), mjEditProcessErrors) || null;
    try {
      await updateTaskJob({
        jobId: snapshotId,
        body: {
          name: mjEditJobName.trim(),
          employee_number_from: mjEditBaselineFrom,
          employee_number_to: employeeTo,
          major_job: activeMajor.id,
          bot_name: updatedBot,
          additional_options: editAddOpts,
          ...(isUploadEdit
            ? {
                document_type: mjEditDocumentType.trim(),
                upload_as_zip: mjEditUploadAsZip,
                unzip_folder_before_uploading: mjEditUnzipBefore,
                path_to_s3_base_folder: mjEditSourcePath.trim() || undefined,
              }
            : isDeleteEdit
              ? { document_type: "", path_to_s3_base_folder: "" }
              : { document_type: "" }),
          use_ssn_match: mjEditUseSsnMatch,
        },
      }).unwrap();

      setMjAccordionTasksByJob((prev) => {
        delete prev[snapshotId];
        return { ...prev };
      });
      await safeRefetchChildJobs();
      await refetch();
      if (mjManageJobId === snapshotId) {
        void dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(snapshotId, { subscribe: false, forceRefetch: true }),
        )
          .unwrap()
          .then((r) => setMjManageJobDetail(r as Record<string, unknown>))
          .catch(() => {});
      }
      setMjEditJobOpen(false);
      if (expandedMjJobAccordion[snapshotId]) await reloadMjAccordionTasks(snapshotId);
    } catch (e) {
      setMjEditJobError(getErr(e, "Failed to save task job."));
    }
  };

  useEffect(() => {
    setSelectedMajorIds([]);
    setActiveMajorId(null);
    setSelectedChildJobIds([]);
    setExpandedMjJobAccordion({});
    setMjAccordionTasksByJob({});
    setMjJobRangeWarningsByJobId({});
    setMjJobActionAlertsByJobId({});
    setMjNestedTaskSelectionByJob({});
    setMjBatchDeleteNestDialog(null);
    setMjManageJobId(null);
    setMjManageJobDetail(null);
    setMjManageJobError(null);
  }, [userId]);

  useEffect(() => {
    setSelectedChildJobIds([]);
    setPoolConnSelectedIds([]);
    if (activeMajorId != null && !majors.some((m) => m.id === activeMajorId)) setActiveMajorId(null);
  }, [majors, activeMajorId]);

  // Drop any selected connection ids that no longer belong to this major job's
  // pool (e.g. a connection was deleted elsewhere or vendor switched).
  useEffect(() => {
    setPoolConnSelectedIds((prev) => prev.filter((id) => poolConnIdsForMajor.includes(id)));
  }, [poolConnIdsForMajor]);

  useEffect(() => {
    if (detailMajorJobId == null) return;
    if (majorsStillLoading) return;
    if (!majors.some((m) => m.id === detailMajorJobId)) return;
    setActiveMajorId(detailMajorJobId);
  }, [detailMajorJobId, majors, majorsStillLoading]);

  const toggleMajorCheckbox = (id: number, checked: boolean) => {
    setSelectedMajorIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const majorIdsOnPage = useMemo(() => majors.map((m) => m.id), [majors]);
  const allMajorsOnPageSelected =
    majorIdsOnPage.length > 0 && majorIdsOnPage.every((id) => selectedMajorIds.includes(id));
  const someMajorsOnPageSelected = majorIdsOnPage.some((id) => selectedMajorIds.includes(id));

  const toggleSelectAllMajorsOnPage = (checked: boolean) => {
    setSelectedMajorIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...majorIdsOnPage]));
      }
      const drop = new Set(majorIdsOnPage);
      return prev.filter((id) => !drop.has(id));
    });
  };

  const openCreateMajor = () => {
    setMajorEditTarget(null);
    setMajName("");
    setMajVendor("");
    setMajKind("download_bots");
    setMajorFormError(null);
    setMajorEditOpen(true);
  };

  const openEditMajor = (m: MajorJob) => {
    const src = majors.find((x) => x.id === m.id) ?? m;
    setMajorEditTarget(src);
    setMajName(src.name);
    setMajVendor(src.vendor);
    setMajKind(src.bot_kind);
    setMajorFormError(null);
    setMajorEditOpen(true);
  };

  const saveMajorForm = async () => {
    if (!canEditBots) return;
    if (!majName.trim()) {
      setMajorFormError("Name is required.");
      return;
    }
    if (!majVendor.trim()) {
      setMajorFormError("Vendor is required.");
      return;
    }
    try {
      if (majorEditTarget) {
        await updateMajorJob({
          majorJobId: majorEditTarget.id,
          invalidateUserId: userId,
          body: {
            name: majName.trim(),
            vendor: majVendor.trim(),
            bot_kind: majKind,
          },
        }).unwrap();
      } else {
        await createMajorJob({
          userId,
          name: majName.trim(),
          vendor: majVendor.trim(),
          bot_kind: majKind,
          included_connection_ids: [],
        }).unwrap();
      }
      await refetch();
      setMajorEditOpen(false);
    } catch {
      setMajorFormError("Failed to save major job.");
    }
  };

  const confirmRemoveMajor = async (m: MajorJob) => {
    if (!canEditBots) return;
    try {
      await deleteMajorJob({
        majorJobId: m.id,
        invalidateUserId: userId,
        deleteLogs: majorDeleteLogs,
      }).unwrap();
      if (activeMajorId === m.id) setActiveMajorId(null);
      if (detailMajorJobId === m.id) onExitMajorDetail?.();
      setMajorRowDeleteTarget(null);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const confirmBulkDeleteMajors = async () => {
    if (!canEditBots) return;
    if (!selectedMajorIds.length) return;
    try {
      for (const mid of selectedMajorIds) {
        await deleteMajorJob({
          majorJobId: mid,
          invalidateUserId: userId,
          deleteLogs: majorDeleteLogs,
        }).unwrap();
        if (detailMajorJobId === mid) onExitMajorDetail?.();
      }
      setSelectedMajorIds([]);
      setActiveMajorId(null);
      setMajorBulkDeleteOpen(false);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const applyPool = async (nextIds: number[]) => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    await updateMajorJob({
      majorJobId: activeMajor.id,
      invalidateUserId: userId,
      body: { included_connection_ids: Array.from(new Set(nextIds)).sort((a, b) => a - b) },
    }).unwrap();
    await refetch();
  };

  const linkExistingToMajor = async (connectionId: number) => {
    if (!activeMajor) return;
    const c = connections.find((x) => x.id === connectionId);
    if (!c || normVendor(c.vendor) !== normVendor(activeMajor.vendor)) return;
    const next = Array.from(new Set([...(activeMajor.included_connection_ids ?? []), connectionId]));
    await applyPool(next);
  };

  const openMajConnCreate = () => {
    if (!activeMajor) return;
    setMcUsername("");
    setMcHost("");
    setMcClientCode("");
    setMcPassword("");
    setMcShowPw(false);
    setMcError(null);
    setMajConnCreateOpen(true);
  };

  const submitMajConnCreate = async () => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    setMcError(null);
    if (!mcUsername.trim()) {
      setMcError("Username is required.");
      return;
    }
    try {
      const raw = await createConnectionMutation({
        userId,
        body: {
          vendor: activeMajor.vendor.trim(),
          username: mcUsername.trim(),
          host: mcHost.trim(),
          client_code: mcClientCode.trim(),
          password: mcPassword,
          port: "9223",
        },
      }).unwrap();
      const newId = typeof raw.message?.data?.id === "number" ? raw.message.data.id : null;
      await onRefreshConnections();
      setMajConnCreateOpen(false);
      if (newId != null) await linkExistingToMajor(newId);
    } catch (e) {
      setMcError(getErr(e, "Failed to create connection."));
    }
  };

  const openMajConnEdit = (c: AdminVendorConnection) => {
    setMajConnEdit(c);
    setMeUsername(c.username || "");
    setMeHost(c.host || "");
    setMeClientCode(c.client_code || "");
    setMePassword(c.password || "");
    setMeShowPw(false);
    setMeError(null);
  };

  const submitMajConnEdit = async () => {
    if (!canEditBots) return;
    if (!majConnEdit) return;
    setMeError(null);
    if (!meUsername.trim()) {
      setMeError("Username is required.");
      return;
    }
    try {
      await updateConnectionMutation({
        connectionId: majConnEdit.id,
        body: {
          vendor: majConnEdit.vendor,
          username: meUsername.trim(),
          host: meHost.trim() || null,
          client_code: meClientCode.trim() || null,
          password: mePassword,
          port: "9223",
        },
      }).unwrap();
      await onRefreshConnections();
      setMajConnEdit(null);
    } catch (e) {
      setMeError(getErr(e, "Failed to update connection."));
    }
  };

  const deleteConnectionHard = async (cid: number) => {
    if (!canEditBots) return;
    try {
      await deleteConnectionMutation(cid).unwrap();
      await onRefreshConnections();
      setDeleteConnConfirm(null);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const toggleChildCheckbox = (id: number, checked: boolean) => {
    setSelectedChildJobIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  /** Confirmation dialog state for the bulk "Delete selected task jobs" action. */
  const [bulkChildDeleteOpen, setBulkChildDeleteOpen] = useState(false);

  const openBulkDeleteChildren = () => {
    if (!selectedChildJobIds.length) return;
    setBulkChildDeleteLogs(false);
    setBulkChildDeleteOpen(true);
  };

  const confirmBulkDeleteChildren = async () => {
    if (!canEditBots) return;
    if (!selectedChildJobIds.length) {
      setBulkChildDeleteOpen(false);
      return;
    }
    try {
      await taskJobsBatchDelete({ job_ids: selectedChildJobIds, delete_logs: bulkChildDeleteLogs }).unwrap();
      setSelectedChildJobIds([]);
      setExpandedMjJobAccordion({});
      setMjAccordionTasksByJob({});
      await safeRefetchChildJobs();
      await refetch();
    } catch {
      /* keep dialog open on error so the user notices via the alerts strip */
    } finally {
      setBulkChildDeleteOpen(false);
    }
  };

  const openCreateChildJob = () => {
    if (!activeMajor) return;
    if (!majorConnPickerIds.length) {
      setChildError(
        `No bot logins exist for vendor "${activeMajor.vendor}" on this user yet. Use the Connections tab to add one.`,
      );
      setChildDialogOpen(true);
      return;
    }
    setChildError(null);
    setChildEmployeesNum("100");
    setTaskJobListName("");
    const firstConn = majorConnPickerIds[0];
    setChildPlans([{ connection_id: firstConn, task_target_count: "1" }]);
    setChildBotName("");
    setChildAdditionalOptions("");
    setChildProcessErrors(false);
    setChildDocumentType("");
    setChildDocumentTypes([""]);
    setChildUploadAsZip(false);
    setChildUnzipBefore(false);
    setChildUseSsnMatch(true);
    setChildSourcePath("");
    setChildDialogOpen(true);
  };

  const createChildSubmit = async () => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    if (!childBotName.trim()) {
      setChildError("Bot is required.");
      return;
    }
    if (!taskJobListName.trim()) {
      setChildError("Task Job Name is required.");
      return;
    }
    const employeesNumber = parseEmployeesNumber(childEmployeesNum);
    if (employeesNumber == null) {
      setChildError("Employees must be a positive integer.");
      return;
    }
    const newTaskJobName = taskJobListName.trim().slice(0, 255);
    const connPlans = normalizePlans(childPlans);
    if (!connPlans.length) {
      setChildError("Add at least one connection row with a task count.");
      return;
    }
    const totalTasks = connPlans.reduce((acc, r) => acc + r.task_target_count, 0);
    if (totalTasks < 1) {
      setChildError("Total tasks must be >= 1.");
      return;
    }
    if (totalTasks > MAX_BATCH_TASKS) {
      setChildError(`Total tasks (${totalTasks}) exceeds the per-job limit of ${MAX_BATCH_TASKS}.`);
      return;
    }
    const vnormCreate = normVendor(activeMajor.vendor);
    for (const r of connPlans) {
      const c = connections.find((x) => x.id === r.connection_id);
      if (!c || normVendor(c.vendor) !== vnormCreate) {
        setChildError(`Connection vendor mismatch for ${activeMajor.vendor}.`);
        return;
      }
    }
    const isUploadKind = activeMajor.bot_kind === "upload_bots";
    const isDeleteKind = activeMajor.bot_kind === "delete_bots";
    if (isUploadKind && !childDocumentType.trim()) {
      setChildError("Document type is required.");
      return;
    }
    if (isDeleteKind) {
      const validTypes = childDocumentTypes.filter((t) => t.trim());
      if (!validTypes.length) {
        setChildError("At least one document type is required.");
        return;
      }
    }
    if (isUploadKind && !childSourcePath.trim()) {
      setChildError("Source folder is required for upload bots. Click Browse to select one.");
      return;
    }
    const docType = isUploadKind ? childDocumentType.trim() : "";
    const folderSlug = extractDefaultFolderSlugFromBotName(childBotName.trim());
    const defaultPath = isUploadKind
      ? childSourcePath.trim()
      : isDeleteKind
        ? ""
        : `users/${userId}/${activeMajor.vendor}/data/${folderSlug}`;
    const addOpts = isDeleteKind
      ? buildDeleteAdditionalOptions(childAdditionalOptions.trim(), childDocumentTypes) || null
      : applySkipErrorEmployees(childAdditionalOptions.trim(), childProcessErrors) || null;
    try {
      await ensureMajorIncludesConnectionIds(connPlans.map((r) => r.connection_id));
      const primaryConn = connPlans[0].connection_id;
      const primaryCount = connPlans[0].task_target_count;
      const res = await createTasksBatch({
        connection: primaryConn,
        bot_name: childBotName.trim(),
        document_type: docType,
        task_status: "PENDING",
        bot_work_status: "NOT_LOGGED_IN",
        path_to_s3_base_folder: defaultPath,
        browser_profile_id: 1,
        upload_as_zip: isUploadKind ? childUploadAsZip : false,
        unzip_folder_before_uploading: isUploadKind ? childUnzipBefore : false,
        use_ssn_match: childUseSsnMatch,
        tasks_number: primaryCount,
        employees_number: employeesNumber,
        employee_number_from: 0,
        use_separate_profiles: true,
        create_job: true,
        job_name: newTaskJobName,
        major_job_id: activeMajor.id,
        additional_options: addOpts,
      }).unwrap();
      const jobId = Number((res as { job?: { id?: unknown } }).job?.id);
      if (!jobId || Number.isNaN(jobId)) throw new Error("missing job id");
      await updateTaskJob({
        jobId,
        body: {
          name: newTaskJobName,
          major_job: activeMajor.id,
          connection_plans: connPlans,
          additional_options: addOpts,
        },
      }).unwrap();
      for (let i = 1; i < connPlans.length; i++) {
        const extra = connPlans[i];
        if (extra.task_target_count < 1) continue;
        await addTasksToJobMutation({
          jobId,
          count: extra.task_target_count,
          connection_id: extra.connection_id,
        }).unwrap();
      }
      await safeRefetchChildJobs();
      await refetch();
      setChildDialogOpen(false);
    } catch (e) {
      setChildError(getErr(e, "Unable to create task job. Check inputs and try again."));
    }
  };

  const mjAddTasksDialogStats = useMemo(() => {
    if (!mjAddTasksOpen || mjAddTasksJobId == null) return null;

    let currentTasks = mjAccordionTasksByJob[mjAddTasksJobId] ?? [];
    const det =
      mjManageJobDetail != null && Number((mjManageJobDetail as Record<string, unknown>).id) === mjAddTasksJobId
        ? mjManageJobDetail
        : null;

    const rawDt = det ? ((det as Record<string, unknown>).tasks as unknown) : undefined;
    if (
      rawDt &&
      Array.isArray(rawDt) &&
      rawDt.length > 0 &&
      rawDt.length >= currentTasks.length
    ) {
      currentTasks = (rawDt as Record<string, unknown>[]).map((t) =>
        normalizeRawTaskToMj(t, mjAddTasksJobId),
      );
    }

    const jRec =
      (mjEffectiveManagedJob != null && Number(mjEffectiveManagedJob.id) === mjAddTasksJobId
        ? mjEffectiveManagedJob
        : null) ??
      (det ? (det as Record<string, unknown>) : null) ??
      (((childJobs.find((r) => Number((r as Record<string, unknown>).id) === mjAddTasksJobId) ??
        null) as Record<string, unknown> | null));

    const plansSnap = jRec ? parseConnectionPlansFromJobRow(jRec) : [];
    const planMode = plansSnap.length > 0;
    const jobTotalRemain = Math.max(0, MAX_BATCH_TASKS - currentTasks.length);

    const resolved = resolveMjBulkTaskAddPayload(
      planMode,
      plansSnap,
      mjAddPlanConnPool,
      mjBulkAddRows,
      currentTasks,
      jobTotalRemain,
      connections,
    );

    // Block adding only on global 30-cap or running tasks. Plan-quota errors
    // surfaced by the resolver are no longer enforced - the backend auto-raises
    // per-connection quotas, so the only hard frontend rule is "<=30 total".
    const jobHasRunningTasks = jRec ? mjJobAggregateRunning(jRec) : false;
    const overGlobalLimit =
      "error" in resolved && /total adds .* exceed/i.test(resolved.error);
    const planLevelOnly = "error" in resolved && !overGlobalLimit;

    const addBlockingReason = overGlobalLimit
      ? resolved.error
      : jobHasRunningTasks
        ? "Stop all tasks of this task job before adding new ones."
        : null;

    const planLevelHint = planLevelOnly ? (resolved as { error: string }).error : null;

    const totalHelp = `${currentTasks.length} / ${MAX_BATCH_TASKS} tasks total - up to ${jobTotalRemain} more allowed on this job.`;

    const countHint =
      jobTotalRemain < 1
        ? `This task job is at its ${MAX_BATCH_TASKS} task limit.`
        : `Up to ${jobTotalRemain} more task(s) can be added.`;

    return {
      plansSnap,
      totalHelp,
      planMode,
      legacyMultiLoginHint: null as string | null,
      addBlockingReason,
      planLevelHint,
      countHint,
      jobHasRunningTasks,
    };
  }, [
    mjAddTasksOpen,
    mjAddTasksJobId,
    mjAccordionTasksByJob,
    mjEffectiveManagedJob,
    mjManageJobDetail,
    childJobs,
    mjBulkAddRows,
    mjAddPlanConnPool,
    majorConnPickerIds,
    connections,
  ]);

  const closeMjManage = () => {
    setMjManageJobId(null);
    setMjManageJobDetail(null);
    setMjManageJobError(null);
    setMatchesMergeSuccess(null);
    resetMatchesMergeState();
  };

  if (isDetailMode && detailMajorNotFound) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
        <Alert severity="warning">This automation was not found (or is not visible for this user).</Alert>
        {onExitMajorDetail && (
          <Button variant="outlined" onClick={onExitMajorDetail}>
            Back to Major Jobs list
          </Button>
        )}
      </Box>
    );
  }

  if (!isDetailMode && majorsStillLoading && majors.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {isDetailMode ? "Major job workspace" : "Major jobs"}
          </Typography>
          {!isDetailMode && (
            // Hidden on phones — the mobile user switcher above already names the user.
            <Box sx={{ mt: 0.75, display: { xs: "none", sm: "block" } }}>
              <MajorJobStripItem
                title="User"
                value={userLabel?.trim() ? userLabel.trim() : `ID ${userId}`}
                avatar
              />
            </Box>
          )}
        </Box>
        {/* Mobile: full-width stacked buttons (drawer-like); desktop: inline row. */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            "& .MuiButton-root": { minHeight: { xs: 40, sm: 0 } },
          }}
        >
          {!isDetailMode && (
            <>
              <Button size="small" variant="contained" onClick={openCreateMajor} disabled={isCreating || !canEditBots}>
                New Major Job
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenConnectionsScreen?.()}
                disabled={!onOpenConnectionsScreen}
              >
                Connections
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => {
                  setMajorDeleteLogs(false);
                  setMajorBulkDeleteOpen(true);
                }}
                disabled={!selectedMajorIds.length || isDeletingMajor || !canEditBots}
              >
                Delete selected
              </Button>
            </>
          )}
          {isDetailMode && onExitMajorDetail && (
            <Button size="small" variant="outlined" onClick={onExitMajorDetail}>
              Back to Major Jobs list
            </Button>
          )}
        </Box>
      </Box>

      {!isDetailMode && (
        <BotsTableScroll fill>
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 900 }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col />
            <col style={{ width: 160 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 130 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                <Checkbox
                  size="small"
                  indeterminate={someMajorsOnPageSelected && !allMajorsOnPageSelected}
                  checked={allMajorsOnPageSelected}
                  disabled={!majors.length}
                  onChange={(e) => toggleSelectAllMajorsOnPage(e.target.checked)}
                  inputProps={{ "aria-label": "Select all major jobs on this page" }}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Name</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Vendor</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Kind</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Connections</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Jobs</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>SSN</TableCell>
              <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {majors.map((m) => (
              <TableRow
                key={m.id}
                hover
                onClick={() => {
                  onOpenMajorDetail?.(m.id);
                }}
                sx={{ cursor: onOpenMajorDetail ? "pointer" : "default" }}
              >
                <TableCell
                  padding="checkbox"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ textAlign: "center", verticalAlign: "middle" }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedMajorIds.includes(m.id)}
                    onChange={(e) => toggleMajorCheckbox(m.id, e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  <Typography variant="body2" noWrap title={m.name}>
                    {m.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  <Typography variant="body2" noWrap title={m.vendor}>
                    {m.vendor}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  {m.bot_kind === "upload_bots" ? "Upload" : m.bot_kind === "delete_bots" ? "Delete" : "Download"}
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>{connections.filter((c) => normVendor(c.vendor) === normVendor(m.vendor)).length}</TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>{m.child_job_ids?.length ?? 0}</TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle" }}>
                  {m.ssn_file_exists === true && (
                    <Tooltip title="SSN file exists">
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditMajor(m);
                      }}
                      disabled={isSavingMajor || !canEditBots}
                    >
                      Edit
                    </Button>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete major job"
                        disabled={!canEditBots}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMajorDeleteLogs(false);
                          setMajorRowDeleteTarget(m);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!majors.length && (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 3 }}>
                  <Typography color="text.secondary">No automations for this user yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </BotsTableScroll>
      )}

      <DeleteJobConfirmDialog
        open={majorRowDeleteTarget != null}
        title="Delete major job?"
        message={
          majorRowDeleteTarget ? (
            <>
              Delete &quot;{majorRowDeleteTarget.name}&quot;? Nested task jobs for this automation will be removed
              (per server rules).
            </>
          ) : null
        }
        deleteData={majorDeleteLogs}
        onDeleteDataChange={setMajorDeleteLogs}
        onCancel={() => setMajorRowDeleteTarget(null)}
        onConfirm={() => {
          if (majorRowDeleteTarget) void confirmRemoveMajor(majorRowDeleteTarget);
        }}
        confirmDisabled={majorRowDeleteTarget == null || !canEditBots}
        busy={isDeletingMajor}
      />

      <DeleteJobConfirmDialog
        open={majorBulkDeleteOpen}
        title="Delete selected major jobs?"
        message={
          <>
            Delete {selectedMajorIds.length} selected major job{selectedMajorIds.length === 1 ? "" : "s"}? Nested task
            jobs will be removed (per server rules).
          </>
        }
        deleteData={majorDeleteLogs}
        onDeleteDataChange={setMajorDeleteLogs}
        onCancel={() => setMajorBulkDeleteOpen(false)}
        onConfirm={() => void confirmBulkDeleteMajors()}
        confirmDisabled={!selectedMajorIds.length || !canEditBots}
        busy={isDeletingMajor}
      />

      {activeMajor && isDetailMode && (
        <Paper variant="outlined" sx={{ p: 1.5, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box sx={{ mb: 1.5, display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {activeMajor.name}
              </Typography>
              {/* Mobile: even 2-column grid of meta pills; desktop: inline wrap. */}
              <Box
                sx={{
                  display: { xs: "grid", sm: "flex" },
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "none" },
                  flexWrap: { sm: "wrap" },
                  gap: 1,
                  alignItems: { sm: "flex-start" },
                  mt: 0.75,
                }}
              >
                <MajorJobStripItem
                  title="User"
                  value={userLabel?.trim() ? userLabel.trim() : `User`}
                  avatar
                />
                <MajorJobStripItem
                  title="Action"
                  value={activeMajor.bot_kind === "upload_bots" ? "Upload" : activeMajor.bot_kind === "delete_bots" ? "Delete" : "Download"}
                />
                <MajorJobStripItem title="Vendor" value={(activeMajor.vendor ?? "").trim() || "-"} />
                {activeMajor.ssn_file_exists != null && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "flex-start",
                      px: 1.25,
                      py: 0.85,
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                      border: 1,
                      borderColor: activeMajor.ssn_file_exists ? "success.main" : "warning.main",
                      boxShadow: (theme) => theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.1, letterSpacing: 0.06, fontWeight: 600 }}>
                        SSN
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.35, color: activeMajor.ssn_file_exists ? "success.main" : "warning.main" }}>
                        {activeMajor.ssn_file_exists ? "ready" : "not ready"}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => openEditMajor(activeMajor)}
                disabled={isSavingMajor || majorHasRunningOrProgress || !canEditBots}
                title={
                  majorHasRunningOrProgress
                    ? "Stop all tasks (no progress) before editing the major job"
                    : ""
                }
                sx={{ flex: { xs: 1, sm: "0 0 auto" }, minHeight: { xs: 40, sm: 0 } }}
              >
                Edit Major Job
              </Button>
              <Tooltip title="Delete Major Job">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Delete major job"
                    onClick={() => {
                      setMajorDeleteLogs(false);
                      setMajorRowDeleteTarget(activeMajor);
                    }}
                    disabled={isDeletingMajor || !canEditBots}
                    sx={{
                      border: { xs: "1px solid", sm: "none" },
                      borderColor: { xs: "divider" },
                      borderRadius: { xs: 1 },
                      minHeight: { xs: 40, sm: 0 },
                      minWidth: { xs: 40, sm: 0 },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          {poolOrphans.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {poolOrphans.length} saved pool id(s) do not match current connections for this vendor.
            </Alert>
          )}
          {(() => {
            const isDeleteKind = activeMajor.bot_kind === "delete_bots";
            return (
              <>
                <Tabs
                  value={manageTab}
                  onChange={handleManageTabChange}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
                >
                  <Tab label="Task jobs" value="jobs" />
                  {!isDeleteKind && <Tab label="SSN Job" value="ssn" />}
                  <Tab label="Connections" value="connections" />
                </Tabs>
                {/* ── SSN Job TabPanel ─────────────────────────────────── */}
                {!isDeleteKind && <TabPanel value={manageTab} index="ssn">
            {ssnJobLoading && <LinearProgress sx={{ mb: 1 }} />}
            {ssnJobError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {ssnJobError}
              </Alert>
            )}
            {!ssnJobLoading && !ssnJobError && ssnJobData !== null && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                {/* SSN File status */}
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>SSN file</Typography>
                    <Box sx={{ flex: 1 }} />
                    {ssnJobData.ssn_file_exists === true && (
                      <>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => activeMajor && void openSsnFileViewer(activeMajor.id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            sx={{ mr: 0.5 }}
                            onClick={() => activeMajor && void downloadSsnFile(activeMajor.id)}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {ssnJobData.ssn_file_exists === true && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={ssnJobFileDeleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
                        disabled={ssnJobFileDeleting || !canEditBots}
                        onClick={async () => {
                          if (!activeMajor || !canEditBots) return;
                          setSsnJobFileDeleting(true);
                          try {
                            await deleteSsnFileMut(activeMajor.id).unwrap();
                            await loadSsnJob(activeMajor.id);
                          } catch { } finally { setSsnJobFileDeleting(false); }
                        }}
                      >
                        Delete file
                      </Button>
                    )}
                  </Stack>
                  {ssnJobData.ssn_file_exists === true ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main">
                        ssn_info.json exists in S3
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {ssnJobData.ssn_file_exists === null
                        ? "SSN file status unknown (S3 not configured)"
                        : "No SSN file in S3"}
                    </Typography>
                  )}
                </Box>

                {/* SSN Job */}
                {ssnJobData.job == null ? (() => {
                  const vendorConns = connections.filter((c) => c.vendor?.toLowerCase() === activeMajor?.vendor?.toLowerCase());
                  const effectiveSsnConnId = ssnJobSelectedConnId ?? (vendorConns.length > 0 ? vendorConns[0].id : null);
                  return (
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, py: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>SSN Job</Typography>
                    <Typography variant="body2" color="text.secondary">No SSN Job created yet.</Typography>
                    <Box sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Connection</InputLabel>
                      <Select
                        value={effectiveSsnConnId ?? ""}
                        label="Connection"
                        onChange={(e) => setSsnJobSelectedConnId(Number(e.target.value) || null)}
                      >
                        {vendorConns.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {(c.username ?? "").trim() ? `@${c.username}` : c.host || String(c.id)}
                          </MenuItem>
                        ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={ssnJobCreating ? <CircularProgress size={14} color="inherit" /> : <Add fontSize="small" />}
                        disabled={ssnJobCreating || !effectiveSsnConnId || !canEditBots}
                        onClick={async () => {
                          if (!activeMajor || !effectiveSsnConnId || !canEditBots) return;
                          setSsnJobCreating(true);
                          try {
                            const res = await createSsnJobMut({ majorJobId: activeMajor.id, connection_id: effectiveSsnConnId }).unwrap();
                            setSsnJobData(res as SsnJobData);
                            setSsnAccordionExpanded(true);
                          } catch (e: unknown) {
                            setSsnJobError((e as { data?: { message?: string } })?.data?.message ?? "Failed to create SSN job.");
                          } finally { setSsnJobCreating(false); }
                        }}
                      >
                        Create
                      </Button>
                  </Box>
                  );
                })() : (() => {
                  const ssnTask = ssnJobData.task;
                  const ssnTaskId = ssnTask ? Number(ssnTask.id) : null;
                  const ssnIsRunning = mjTaskShowsStopInsteadOfRun(String(ssnTask?.task_status ?? ""));
                  const ssnCanRun = canRunTaskByStatusMj(String(ssnTask?.task_status ?? ""));
                  const ssnCurrentCid = typeof ssnTask?.connection === "number" ? ssnTask.connection : null;
                  const ssnDraftCid = ssnTaskId != null ? taskConnDraftById[ssnTaskId] : undefined;
                  const ssnSelectVal = typeof ssnDraftCid === "number" ? ssnDraftCid : (ssnCurrentCid ?? "");
                  const ssnDirty = typeof ssnDraftCid === "number" && ssnDraftCid !== ssnCurrentCid;
                  const ssnSaving = ssnTaskId != null && taskConnSavingId === ssnTaskId;
                  const ssnSaveErr = ssnTaskId != null ? taskConnSaveErrById[ssnTaskId] : undefined;
                  const ssnBusy = ssnTaskId != null && busyTaskId === ssnTaskId;
                  const ssnJobId = Number(ssnJobData.job?.id ?? 0);
                  const ssnAsMjTask: MjConnectionTask = {
                    id: ssnTaskId ?? 0,
                    job: ssnJobId,
                    task_status: String(ssnTask?.task_status ?? ""),
                    bot_work_status: String(ssnTask?.bot_work_status ?? ""),
                    task2fa_status: typeof ssnTask?.task2fa_status === "string" ? ssnTask.task2fa_status : "not_requested",
                    bot_name: String(ssnTask?.bot_name ?? ""),
                    employee_number_from: Number(ssnTask?.employee_number_from ?? 0),
                    employee_number_to: Number(ssnTask?.employee_number_to ?? 0),
                    connection: ssnCurrentCid ?? undefined,
                  };
                  const ssnShowPass2FA = shouldShowPass2FAMj(ssnAsMjTask);
                  return (
                    <Accordion
                      expanded={ssnAccordionExpanded}
                      disableGutters
                      elevation={0}
                      TransitionProps={{ timeout: 0 }}
                      onChange={() => setSsnAccordionExpanded((v) => !v)}
                      sx={MJ_ACCORDION_ROW_SX}
                    >
                      <AccordionSummary
                        sx={{
                          ...MJ_ACC_SUMMARY_SX,
                          px: 2,
                          boxSizing: "border-box",
                          "& .MuiAccordionSummary-content": {
                            ...MJ_ACC_SUMMARY_SX["& .MuiAccordionSummary-content"],
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            margin: "0 !important",
                          },
                        }}
                      >
                        <Typography fontWeight={700} noWrap>SSN Job</Typography>
                        <StatusChipMj
                          label={String(ssnJobData.job?.task_status ?? "PENDING")}
                          color={taskRunStatusChipColor(String(ssnJobData.job?.task_status ?? "PENDING"))}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Refresh">
                            <span>
                              <IconButton size="small" color="primary" onClick={async (e) => { e.stopPropagation(); if (activeMajor) await loadSsnJob(activeMajor.id); }}>
                                {ssnJobLoading ? <CircularProgress size={18} color="primary" /> : <ReplayIcon sx={{ fontSize: 20 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete SSN job">
                            <span>
                              <IconButton size="small" color="error" disabled={ssnJobDeleting || ssnIsRunning || !canEditBots}
                                onClick={(e) => { e.stopPropagation(); setSsnJobDeleteConfirmOpen(true); }}>
                                {ssnJobDeleting ? <CircularProgress size={16} color="error" /> : <DeleteOutlineIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={ssnAccordionExpanded ? "Collapse" : "Expand"}>
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setSsnAccordionExpanded((v) => !v); }}>
                              <ExpandMore sx={{ fontSize: 24, transition: "transform 0.2s", transform: ssnAccordionExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 1, pb: 2, px: 2, borderTop: 1, borderColor: "divider" }}>
                        {ssnTask && (
                          <BotsTableScroll>
                          <Table size="small" sx={{ tableLayout: "fixed", width: "100%", minWidth: 760 }}>
                            <colgroup>
                              <col style={{ width: "48px" }} />
                              <col style={{ width: "145px" }} />
                              <col style={{ width: "145px" }} />
                              <col />
                              <col style={{ width: "160px" }} />
                            </colgroup>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>№</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Bot</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Connection</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell align="center">1</TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <StatusChipMj label={String(ssnTask.task_status ?? "")} color={taskRunStatusChipColor(String(ssnTask.task_status ?? ""))} />
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <StatusChipMj label={String(ssnTask.bot_work_status ?? "")} color={botWorkStatusChipColor(String(ssnTask.bot_work_status ?? ""))} />
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle", py: 1.25, overflow: "hidden" }}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                                    <Tooltip title={ssnIsRunning ? "Stop the task first to move it to another login" : ""}>
                                      <TextField
                                        select
                                        size="small"
                                        value={ssnSelectVal}
                                        onChange={(ev) => {
                                          const v = Number(ev.target.value);
                                          if (Number.isFinite(v) && v > 0 && ssnTaskId != null) setTaskConnDraft(ssnTaskId, v);
                                        }}
                                        disabled={ssnIsRunning || ssnSaving || !majorConnPickerIds.length || !canEditBots}
                                        sx={{ width: 200, "& .MuiInputBase-input": { textAlign: "left" } }}
                                        error={Boolean(ssnSaveErr)}
                                        helperText={ssnSaveErr ?? ""}
                                        FormHelperTextProps={{ sx: { mx: 0, textAlign: "left" } }}
                                      >
                                        {majorConnPickerIds.map((cid, ix) => (
                                          <MenuItem key={cid} value={cid}>{`${ix + 1}. ${connectionPoolRowLabel(connections, cid) || "-"}`}</MenuItem>
                                        ))}
                                        {typeof ssnCurrentCid === "number" && !majorConnPickerIds.includes(ssnCurrentCid) && (
                                          <MenuItem value={ssnCurrentCid}>{connectionPoolRowLabel(connections, ssnCurrentCid) || "-"}</MenuItem>
                                        )}
                                      </TextField>
                                    </Tooltip>
                                    {ssnDirty && ssnTaskId != null && (
                                      <Stack direction="row" spacing={0.25} alignItems="center">
                                        <Button size="small" variant="contained" disabled={ssnSaving || ssnIsRunning || !canEditBots}
                                          onClick={() => { const t = { id: ssnTaskId, task_status: String(ssnTask.task_status), bot_work_status: String(ssnTask.bot_work_status), bot_name: String(ssnTask.bot_name ?? ""), employee_number_from: Number(ssnTask.employee_number_from), employee_number_to: Number(ssnTask.employee_number_to), connection: ssnCurrentCid }; void saveTaskConn(ssnJobId, t as MjConnectionTask, ssnDraftCid!); }}>
                                          {ssnSaving ? "..." : "Save"}
                                        </Button>
                                        <Button size="small" disabled={ssnSaving} onClick={() => ssnTaskId != null && clearTaskConnDraft(ssnTaskId)}>Cancel</Button>
                                      </Stack>
                                    )}
                                  </Stack>
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                  <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                    <Tooltip title="Open task workspace">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          aria-label="Task details"
                                          disabled={!ssnTaskId}
                                          onClick={() => {
                                            if (!ssnTaskId) return;
                                            const back = encodeURIComponent(location.pathname + location.search);
                                            navigate(
                                              `/bots-management/${userId}/${ssnCurrentCid ?? ""}/task/${ssnTaskId}?back=${back}`,
                                            );
                                          }}
                                        >
                                          <ReadMoreIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {ssnShowPass2FA && (
                                      <Tooltip title="Enter the 2FA code">
                                        <span>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            sx={MJ_PASS_2FA_BTN_SX}
                                            startIcon={<VpnKeyOutlinedIcon fontSize="small" />}
                                            onClick={() => {
                                              setMjAccordionTasksByJob((prev) => ({ ...prev, [ssnJobId]: [ssnAsMjTask] }));
                                              setTwoFaMjTask(ssnAsMjTask);
                                              setTwoFaMjCode("");
                                              setTwoFaMjErr(null);
                                            }}
                                          >
                                            2FA
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    )}
                                    {ssnIsRunning ? (
                                      <Tooltip title="Stop task">
                                        <span>
                                          <IconButton size="small" color="error" disabled={ssnBusy || !canEditBots}
                                            onClick={() => { if (ssnTaskId) void mjStopTaskRow(ssnJobId, ssnTaskId); }}>
                                            {ssnBusy ? <CircularProgress size={14} color="error" /> : <StopIcon fontSize="small" />}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Run task">
                                        <span>
                                          <IconButton size="small" color="success" disabled={ssnBusy || !ssnCanRun || !canEditBots}
                                            onClick={() => { if (ssnTaskId) void mjRunTaskRow(ssnJobId, ssnAsMjTask); }}>
                                            {ssnBusy ? <CircularProgress size={14} color="success" /> : <PlayArrowIcon fontSize="small" />}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                          </BotsTableScroll>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </Box>
            )}
            {!ssnJobLoading && !ssnJobError && ssnJobData === null && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <Button variant="outlined" size="small" onClick={() => activeMajor && loadSsnJob(activeMajor.id)}>
                  Load SSN Job
                </Button>
              </Box>
            )}
          </TabPanel>}
                <TabPanel value={manageTab} index="connections">
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Available connections
                  </Typography>
            {connectionsError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Failed to load connections.
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mb: 1.5,
                "& .MuiButton-root": {
                  flex: { xs: 1, sm: "0 0 auto" },
                  minHeight: { xs: 40, sm: 0 },
                },
              }}
            >
              <Button size="small" variant="contained" onClick={openMajConnCreate} disabled={!activeMajor || !canEditBots}>
                New connection
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => setPoolConnBulkDeleteOpen(true)}
                disabled={!poolConnSelectedIds.length || poolConnBulkDeleting || !canEditBots}
              >
                Delete selected
              </Button>
            </Box>
            {connectionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <BotsTableScroll>
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 860 }}>
                  <colgroup>
                    <col style={{ width: 48 }} />
                    <col style={{ width: 60 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 130 }} />
                    <col />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 150 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                        <Checkbox
                          size="small"
                          checked={
                            poolConnIdsForMajor.length > 0 &&
                            poolConnIdsForMajor.every((id) => poolConnSelectedIds.includes(id))
                          }
                          indeterminate={
                            poolConnIdsForMajor.some((id) => poolConnSelectedIds.includes(id)) &&
                            !poolConnIdsForMajor.every((id) => poolConnSelectedIds.includes(id))
                          }
                          disabled={!poolConnIdsForMajor.length}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPoolConnSelectedIds((prev) => {
                              if (checked) {
                                return Array.from(new Set([...prev, ...poolConnIdsForMajor]));
                              }
                              const drop = new Set(poolConnIdsForMajor);
                              return prev.filter((id) => !drop.has(id));
                            });
                          }}
                          inputProps={{ "aria-label": "Select all linked connections" }}
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>№</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Login</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Vendor</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Host</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Client code</TableCell>
                      <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poolConnIdsForMajor.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 3 }}>
                          <Typography color="text.secondary">
                            No vendor-matching bot connections linked to this automation yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      poolConnIdsForMajor.map((cid, idx) => {
                        const row = connections.find((c) => c.id === cid);
                        const usernameLabel = row?.username?.trim()
                          ? `@${row.username.trim()}`
                          : "-";
                        const hostLabel =
                          row?.host?.trim() || `default ${row?.vendor?.trim() || "vendor"} host`;
                        return (
                          <TableRow key={cid} hover>
                            <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                              <Checkbox
                                size="small"
                                checked={poolConnSelectedIds.includes(cid)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setPoolConnSelectedIds((prev) =>
                                    checked ? [...prev, cid] : prev.filter((x) => x !== cid),
                                  );
                                }}
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>{idx + 1}</TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography variant="body2" noWrap title={usernameLabel}>
                                {row ? usernameLabel : "..."}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography variant="body2" noWrap title={row?.vendor ?? ""}>
                                {row?.vendor ?? "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                noWrap
                                title={hostLabel}
                                color={row?.host?.trim() ? "text.primary" : "text.secondary"}
                              >
                                {hostLabel}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography variant="body2" noWrap title={row?.client_code ?? ""}>
                                {row?.client_code?.trim() || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="center"
                                alignItems="center"
                              >
                                {row && (
                                  <Button size="small" disabled={!canEditBots} onClick={() => openMajConnEdit(row)}>
                                    Edit
                                  </Button>
                                )}
                                <Tooltip title="Delete connection">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      aria-label="Delete connection"
                                      disabled={!canEditBots}
                                      onClick={() => setDeleteConnConfirm(cid)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                </BotsTableScroll>
              )}
                </TabPanel>
              </>
            );
          })()}
          <TabPanel value={manageTab} index="jobs">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Task jobs
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  ml: "auto",
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiButton-root": {
                    flex: { xs: 1, sm: "0 0 auto" },
                    minHeight: { xs: 40, sm: 0 },
                  },
                }}
              >
                <Button size="small" variant="contained" onClick={openCreateChildJob} disabled={!canEditBots}>
                  Add Task Job
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={openBulkDeleteChildren}
                  disabled={selectedChildJobIds.length === 0 || isDeletingJobsBulk || !canEditBots}
                >
                  Delete selected
                </Button>
              </Box>
            </Box>

            <BotsTableScroll>
              <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: MJ_JOBS_TABLE_MIN_WIDTH }}>
                <colgroup>
                  <col style={{ width: 48 }} />
                  <col />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 56 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                      <Checkbox
                        size="small"
                        indeterminate={taskJobRowsSelectIndeterminate}
                        checked={allTaskJobRowsSelected}
                        disabled={!taskJobRowIdsOnPage.length}
                        onChange={(e) => toggleSelectAllTaskJobRows(e.target.checked)}
                        inputProps={{ "aria-label": "Select all task jobs in this list" }}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BOTS_HEADER_CELL_SX, textAlign: "left" }}>Name</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Status</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Tasks</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Total</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Success</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Error</TableCell>
                    <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                    <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX} />
                  </TableRow>
                </TableHead>
                <TableBody>
              {childJobs.filter((raw) => !isSSNJob(raw as Record<string, unknown>)).map((raw) => {
                const baseRow = raw as Record<string, unknown>;
                const id = Number(baseRow.id);
                const row = { ...baseRow, ...(mjJobDetailById[id] ?? {}) };
                const employeeStats = mjEmployeeStatsFromJob(row);
                const expanded = Boolean(expandedMjJobAccordion[id]);
                const tasks = mjAccordionTasksByJob[id] ?? [];
                const nestSel = mjNestedTaskSelectionByJob[id] ?? [];
                const allNestIds = tasks.map((t) => t.id);
                const allNestSelected = allNestIds.length > 0 && allNestIds.every((tid) => nestSel.includes(tid));
                const { running, total } = mjJobStatsTotals(row);
                const busyRow = Boolean(mjRefreshingJobIds[id]);
                return (
                  <Fragment key={id}>
                    <TableRow
                      hover
                      selected={expanded}
                      onClick={(e) => handleMjAccordionExpanded(e, id, !expanded)}
                      sx={{
                        cursor: "pointer",
                        ...(expanded ? { "& > td": { borderBottom: 0 } } : {}),
                      }}
                    >
                      <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedChildJobIds.includes(id)}
                          onChange={(e) => toggleChildCheckbox(id, e.target.checked)}
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <Typography fontWeight={700} noWrap title={mjJobDisplayName(row)}>
                          {mjJobDisplayName(row)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BOTS_BODY_CELL_SX}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <StatusChipMj
                            label={String(row.task_status ?? "")}
                            color={taskRunStatusChipColor(String(row.task_status ?? ""))}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Typography variant="body2" color="text.secondary">
                          {running}/{total}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Typography variant="body2" color="text.secondary">
                          {employeeStats.total}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Tooltip title={mjStatsSnapshotTooltip(employeeStats.snapshotAt)}>
                          <Typography variant="body2" color="text.secondary">
                            {employeeStats.success}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ ...BOTS_BODY_CELL_SX, fontVariantNumeric: "tabular-nums" }}>
                        <Tooltip title={mjStatsSnapshotTooltip(employeeStats.snapshotAt)}>
                          <Typography variant="body2" color="text.secondary">
                            {employeeStats.error}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{ ...BOTS_BODY_CELL_SX, whiteSpace: "nowrap" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Tooltip title="Refresh job row — re-reads stored logs so totals include deleted tasks">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={busyRow}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  void refreshMjJobRow(id);
                                }}
                              >
                                {busyRow ? (
                                  <CircularProgress size={18} color="primary" />
                                ) : (
                                  <ReplayIcon sx={{ fontSize: 20 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {activeMajor.bot_kind !== "delete_bots" && (
                            <Button
                              size="small"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleMjViewJobFiles(row);
                              }}
                            >
                              Files
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX}>
                        <IconButton size="small" aria-label={expanded ? "Collapse task job" : "Expand task job"}>
                          <ExpandMore
                            sx={{
                              color: "primary.main",
                              fontSize: 24,
                              transition: "transform 150ms",
                              transform: expanded ? "rotate(180deg)" : "none",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, borderBottom: expanded ? 1 : 0, borderColor: "divider" }}>
                        <Collapse in={expanded} timeout={0} unmountOnExit>
                          <Box sx={{ pt: 1, pb: 2, px: 2, borderTop: 1, borderColor: "divider" }}>
                      {mjJobRangeWarningsByJobId[id] ? (
                        <Alert
                          severity="warning"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobRangeWarningsByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobRangeWarningsByJobId[id]}
                        </Alert>
                      ) : null}
                      {mjJobActionAlertsByJobId[id] ? (
                        <Alert
                          severity="error"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobActionAlertsByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobActionAlertsByJobId[id]}
                        </Alert>
                      ) : null}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          justifyContent: "space-between",
                        }}
                      >
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center" useFlexGap>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={String(row.bot_name ?? "-")}
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            color="default"
                            label={`Range ${String(row.employee_number_from ?? 0)}-${String(row.employee_number_to ?? 0)}`}
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ ml: "auto", rowGap: 0.75 }}>
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={nestSel.length === 0 || mjJobActionBusy || !canEditBots}
                            onClick={() =>
                              nestSel.length > 0 && setMjBatchDeleteNestDialog({ jobId: id, ids: [...nestSel] })
                            }
                          >
                            Delete selected ({nestSel.length})
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArticleOutlinedIcon fontSize="small" />}
                            onClick={() => openStatusLogs(id, String(baseRow.name ?? ""))}
                          >
                            Logs
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => openMjManage(id)}>
                            Manage job
                          </Button>
                        </Stack>
                      </Box>
                      {mjAccordionLoadingJobId === id ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <CircularProgress size={22} />
                        </Box>
                      ) : (
                        <BotsTableScroll>
                          <Table
                            size="small"
                            stickyHeader
                            sx={{ tableLayout: "fixed", minWidth: MJ_TASKS_TABLE_MIN_WIDTH }}
                          >
                            <colgroup>
                              <col style={{ width: 44 }} />
                              <col style={{ width: 50 }} />
                              <col style={{ width: 150 }} />
                              <col style={{ width: 150 }} />
                              <col style={{ width: 110 }} />
                              <col style={{ width: 300 }} />
                              <col style={{ width: 160 }} />
                            </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                                <Checkbox
                                  size="small"
                                  indeterminate={nestSel.length > 0 && !allNestSelected}
                                  checked={allNestSelected}
                                  onChange={(e) => mjToggleAllNestedTasks(id, tasks, e.target.checked)}
                                  inputProps={{ "aria-label": "Select all tasks in this job" }}
                                />
                              </TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>№</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Status</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Bot</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Range</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Connection</TableCell>
                              <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tasks.map((task, ti) => (
                              <TableRow key={task.id}>
                                <TableCell padding="checkbox" align="center">
                                  <Checkbox
                                    size="small"
                                    checked={nestSel.includes(task.id)}
                                    onChange={(e) => mjToggleNestedTaskSelect(id, task.id, e.target.checked)}
                                  />
                                </TableCell>
                                <TableCell align="center">{ti + 1}</TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    {(() => {
                                      const s = String(task.task_status ?? "").toUpperCase();
                                      const isErrorChip = s === "CRITICAL_ERROR";
                                      return (
                                        <StatusChipMj
                                          label={task.task_status}
                                          color={taskRunStatusChipColor(task.task_status)}
                                          onClick={
                                            isErrorChip
                                              ? () => setMjErrorPopupTask({ jobId: id, task })
                                              : undefined
                                          }
                                          icon={isErrorChip ? <ErrorOutlineIcon /> : undefined}
                                          title={
                                            isErrorChip
                                              ? "Click to view the error and rerun the task"
                                              : undefined
                                          }
                                        />
                                      );
                                    })()}
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <StatusChipMj
                                      label={task.bot_work_status}
                                      color={botWorkStatusChipColor(task.bot_work_status)}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    verticalAlign: "middle",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {task.employee_number_from}-{task.employee_number_to}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "top", py: 1.25 }}>
                                  {(() => {
                                    const currentCid = task.connection ?? null;
                                    const draftCid = taskConnDraftById[task.id];
                                    const selectVal =
                                      typeof draftCid === "number"
                                        ? draftCid
                                        : typeof currentCid === "number"
                                          ? currentCid
                                          : "";
                                    const dirty =
                                      typeof draftCid === "number" && draftCid !== currentCid;
                                    const taskIsRunning = mjTaskShowsStopInsteadOfRun(
                                      task.task_status,
                                    );
                                    const saving = taskConnSavingId === task.id;
                                    const saveErr = taskConnSaveErrById[task.id];
                                    return (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                          width: "100%",
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          spacing={0.75}
                                          alignItems="flex-start"
                                          justifyContent="center"
                                          flexWrap="wrap"
                                          sx={{ maxWidth: "100%" }}
                                        >
                                        <Tooltip
                                          title={
                                            taskIsRunning
                                              ? "Stop the task first to move it to another login"
                                              : ""
                                          }
                                        >
                                          <TextField
                                            select
                                            size="small"
                                            value={selectVal}
                                            onChange={(ev) => {
                                              const v = Number(ev.target.value);
                                              if (Number.isFinite(v) && v > 0)
                                                setTaskConnDraft(task.id, v);
                                            }}
                                            disabled={
                                              taskIsRunning ||
                                              saving ||
                                              !majorConnPickerIds.length ||
                                              !canEditBots
                                            }
                                            sx={{
                                              width: 220,
                                              maxWidth: "100%",
                                              flexShrink: 0,
                                              "& .MuiInputBase-input": { textAlign: "left" },
                                              "& .MuiSelect-select": {
                                                textAlign: "left",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-start",
                                              },
                                            }}
                                            error={Boolean(saveErr)}
                                            helperText={saveErr ?? ""}
                                            FormHelperTextProps={{ sx: { mx: 0, textAlign: "left" } }}
                                          >
                                            {majorConnPickerIds.map((cid, ix) => (
                                              <MenuItem key={cid} value={cid}>
                                                {`${ix + 1}. ${
                                                  connectionPoolRowLabel(connections, cid) || "-"
                                                }`}
                                              </MenuItem>
                                            ))}
                                            {typeof currentCid === "number" &&
                                              !majorConnPickerIds.includes(currentCid) && (
                                                <MenuItem value={currentCid}>
                                                  {connectionPoolRowLabel(
                                                    connections,
                                                    currentCid,
                                                  ) || "-"}
                                                </MenuItem>
                                              )}
                                          </TextField>
                                        </Tooltip>
                                        {dirty ? (
                                          <Stack direction="row" spacing={0.25}>
                                            <Tooltip title="Save new connection">
                                              <span>
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  disabled={saving || taskIsRunning || !canEditBots}
                                                  onClick={() =>
                                                    void saveTaskConn(id, task, draftCid)
                                                  }
                                                >
                                                  {saving ? "..." : "Save"}
                                                </Button>
                                              </span>
                                            </Tooltip>
                                            <Tooltip title="Discard">
                                              <span>
                                                <Button
                                                  size="small"
                                                  onClick={() => clearTaskConnDraft(task.id)}
                                                  disabled={saving}
                                                >
                                                  Cancel
                                                </Button>
                                              </span>
                                            </Tooltip>
                                          </Stack>
                                        ) : null}
                                      </Stack>
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{ width: "100%", flexWrap: "nowrap" }}
                                  >
                                    <Tooltip title="Open task workspace">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          aria-label="Task details"
                                          onClick={() => {
                                            const back = encodeURIComponent(location.pathname + location.search);
                                            navigate(
                                              `/bots-management/${userId}/${task.connection ?? ""}/task/${task.id}?back=${back}`,
                                            );
                                          }}
                                        >
                                          <ReadMoreIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {shouldShowPass2FAMj(task) && (
                                      <Tooltip title="Enter the 2FA code">
                                        <span>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            sx={MJ_PASS_2FA_BTN_SX}
                                            startIcon={<VpnKeyOutlinedIcon fontSize="small" />}
                                            onClick={() => {
                                              setTwoFaMjTask(task);
                                              setTwoFaMjCode("");
                                              setTwoFaMjErr(null);
                                            }}
                                          >
                                            Pass 2FA
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    )}
                                    {mjTaskShowsStopInsteadOfRun(task.task_status) ? (
                                      <Tooltip title="Stop task">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            disabled={mjJobActionBusy || !canEditBots}
                                            onClick={() => void mjStopTaskRow(id, task.id)}
                                            aria-label="Stop task"
                                          >
                                            {busyTaskId === task.id ? (
                                              <CircularProgress size={14} color="error" />
                                            ) : (
                                              <StopIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Run task">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="success"
                                            disabled={
                                              mjJobActionBusy ||
                                              !canRunTaskByStatusMj(task.task_status) ||
                                              !canEditBots
                                            }
                                            onClick={() => void mjRunTaskRow(id, task)}
                                            aria-label="Run task"
                                          >
                                            {busyTaskId === task.id ? (
                                              <CircularProgress size={14} color="success" />
                                            ) : (
                                              <PlayArrowIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                    <Tooltip
                                      title={
                                        mjTaskShowsStopInsteadOfRun(task.task_status)
                                          ? "Stop the task before deleting"
                                          : "Delete task"
                                      }
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          aria-label="Delete task"
                                          disabled={mjTaskShowsStopInsteadOfRun(task.task_status) || !canEditBots}
                                          onClick={() => setMjDeleteSingleTaskId(task.id)}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!tasks.length && (
                              <TableRow>
                                <TableCell colSpan={7}>
                                  <Typography color="text.secondary">No tasks loaded.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                        </BotsTableScroll>
                      )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
                  {!childJobs.length && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: "center", py: 3 }}>
                        <Typography color="text.secondary">No child jobs yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </BotsTableScroll>
          </TabPanel>
        </Paper>
      )}

      {!activeMajor && !isDetailMode && (
        <Typography color="text.secondary">Select an automation row to manage connections and jobs.</Typography>
      )}

      <Dialog open={majorEditOpen} onClose={() => setMajorEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{majorEditTarget ? "Edit Major Job" : "New Major Job"}</DialogTitle>
        <DialogContent>
          {/* Critical fields (vendor + bot kind) are locked once any task of this major job
              has downloaded any employees - changing them would cause data drift. */}
          {majorEditTarget && majorHasRunningOrProgress ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This major job has running tasks or downloaded employees. Vendor and bot scope cannot be
              changed; stop all tasks and clear progress to unlock them.
            </Alert>
          ) : null}
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            label="Display name"
            required
            value={majName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMajName(e.target.value)}
          />
          <Autocomplete
            freeSolo
            sx={{ mt: 1 }}
            options={majorFormVendorOptions}
            inputValue={majVendor}
            disabled={Boolean(majorEditTarget) && majorHasRunningOrProgress}
            onInputChange={(_e, v) => setMajVendor(v)}
            ListboxProps={MAJOR_FORM_LISTBOX}
            filterOptions={filterMajorFormVendors}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vendor"
                required
                helperText="Task jobs use connections with this vendor."
              />
            )}
          />
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            select
            label="Bot scope"
            value={majKind}
            disabled={Boolean(majorEditTarget) && majorHasRunningOrProgress}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMajKind(e.target.value)}
          >
            <MenuItem value="download_bots">Download</MenuItem>
            <MenuItem value="upload_bots">Upload</MenuItem>
            <MenuItem value="delete_bots">Delete</MenuItem>
          </TextField>
          {majorFormError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {majorFormError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMajorEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveMajorForm()} disabled={isSavingMajor || isCreating || !canEditBots}>
            {isSavingMajor || isCreating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={majConnCreateOpen} onClose={() => setMajConnCreateOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: DIALOG_MINOR_SX }}>
        <DialogTitle>New connection ({activeMajor?.vendor ?? "…"})</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Vendor" value={activeMajor?.vendor ?? ""} disabled sx={{ mt: 1, mb: 1.5 }} />
          <TextField
            required
            fullWidth
            label="Username"
            value={mcUsername}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMcUsername(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Host"
            value={mcHost}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMcHost(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Client code"
            value={mcClientCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMcClientCode(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Password"
            type={mcShowPw ? "text" : "password"}
            value={mcPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMcPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setMcShowPw((v) => !v)} aria-label="toggle password">
                    {mcShowPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {mcError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {mcError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMajConnCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitMajConnCreate()} disabled={isMpcCreating || !canEditBots}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={majConnEdit != null} onClose={() => setMajConnEdit(null)} fullWidth maxWidth="sm" PaperProps={{ sx: DIALOG_MINOR_SX }}>
        <DialogTitle>Edit connection</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Vendor" value={majConnEdit?.vendor ?? ""} disabled sx={{ mt: 1, mb: 1.5 }} />
          <TextField
            required
            fullWidth
            label="Username"
            value={meUsername}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMeUsername(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Host"
            value={meHost}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMeHost(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Client code"
            value={meClientCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMeClientCode(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="Password"
            type={meShowPw ? "text" : "password"}
            value={mePassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMePassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setMeShowPw((v) => !v)} aria-label="toggle password">
                    {meShowPw ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {meError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {meError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMajConnEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitMajConnEdit()} disabled={isMpcUpdating || !canEditBots}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConnConfirm != null} onClose={() => setDeleteConnConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete connection</DialogTitle>
        <DialogContent>
          <Typography>
            Remove this connection from the user? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConnConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isMpcDeleting || !canEditBots}
            onClick={() => deleteConnConfirm != null && void deleteConnectionHard(deleteConnConfirm)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={poolConnBulkDeleteOpen}
        onClose={() => !poolConnBulkDeleting && setPoolConnBulkDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete selected connections?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete {poolConnSelectedIds.length} selected connection
            {poolConnSelectedIds.length === 1 ? "" : "s"}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPoolConnBulkDeleteOpen(false)}
            disabled={poolConnBulkDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!poolConnSelectedIds.length || poolConnBulkDeleting || !canEditBots}
            onClick={async () => {
              if (!poolConnSelectedIds.length || !canEditBots) return;
              setPoolConnBulkDeleting(true);
              try {
                for (const cid of poolConnSelectedIds) {
                  await deleteConnectionMutation(cid).unwrap();
                }
                setPoolConnSelectedIds([]);
                setPoolConnBulkDeleteOpen(false);
                await onRefreshConnections();
                await refetch();
              } catch {
                /* keep dialog open so the user can retry; backend message
                   surfaces via the connection list refresh */
              } finally {
                setPoolConnBulkDeleting(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={childDialogOpen} onClose={() => setChildDialogOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: DIALOG_MINOR_SX }}>
        <DialogTitle>New task job</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1, mb: 1 }}
            label="Task Job Name"
            required
            fullWidth
            value={taskJobListName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setTaskJobListName(e.target.value)}
          />
          <TextField
            sx={{ mb: 1 }}
            label="Employees"
            fullWidth
            value={childEmployeesNum}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setChildEmployeesNum(sanitizeEmployeesInput(e.target.value))}
          />
          <Autocomplete
            sx={{ mb: 1 }}
            options={normalizedChildBots}
            value={
              normalizedChildBots.find((b) => botNameFromRegistry(b) === childBotName) ?? null
            }
            onChange={(_e, v) => setChildBotName(v ? botNameFromRegistry(v) : "")}
            filterOptions={filterChildDialogBots}
            getOptionLabel={(o) => o.bot_slug}
            renderOption={(props, option) => (
              <li {...props} key={`${option.vendor}.${option.job_kind}.${option.bot_slug}`}>
                {option.bot_slug}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Bot" required error={!childBotName && Boolean(childError)} />
            )}
          />
          <TextField
            sx={{ mb: 1 }}
            label="Additional options (applied to all tasks)"
            fullWidth
            multiline
            minRows={2}
            value={childAdditionalOptions}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setChildAdditionalOptions(e.target.value)}
          />
          {activeMajor?.bot_kind !== "delete_bots" && (
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Switch
                  checked={childProcessErrors}
                  onChange={(e) => setChildProcessErrors(e.target.checked)}
                />
              }
              label="Process error employees"
            />
          )}
          {activeMajor?.bot_kind === "upload_bots" && (
            <TextField
              label="Document type *"
              size="small"
              fullWidth
              value={childDocumentType}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setChildDocumentType(e.target.value)}
              sx={{ mb: 1 }}
              placeholder="e.g. w2, paystubs, 1095"
            />
          )}
          {activeMajor?.bot_kind === "delete_bots" && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Document types to delete *
              </Typography>
              {childDocumentTypes.map((dt, i) => (
                <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. Paylocity Historical W2s"
                    value={dt}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setChildDocumentTypes((prev) => prev.map((v, j) => j === i ? e.target.value : v))
                    }
                  />
                  <IconButton size="small" color="error" disabled={childDocumentTypes.length === 1}
                    onClick={() => setChildDocumentTypes((prev) => prev.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Add fontSize="small" />}
                onClick={() => setChildDocumentTypes((prev) => [...prev, ""])}>
                Add type
              </Button>
            </Box>
          )}
          {activeMajor?.bot_kind === "upload_bots" && (
            <Box sx={{ mb: 1, mt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Upload options
              </Typography>
              <Stack direction="column" spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={childUploadAsZip}
                      onChange={(e) => setChildUploadAsZip(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Upload as ZIP"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={childUnzipBefore}
                      onChange={(e) => setChildUnzipBefore(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Unzip before uploading"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={childUseSsnMatch}
                      onChange={(e) => setChildUseSsnMatch(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Use SSN match"
                />
              </Stack>
            </Box>
          )}
          {activeMajor?.bot_kind === "upload_bots" && (
            <Box sx={{ mb: 1.5, mt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Source folder (files to upload)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Select a folder from Data Storage…"
                  value={childSourcePath}
                  InputProps={{ readOnly: true }}
                  error={!childSourcePath && Boolean(childError)}
                  sx={{ "& .MuiInputBase-input": { color: childSourcePath ? "text.primary" : "text.disabled" } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FolderOpenIcon />}
                  onClick={() => openFolderPicker("create", childSourcePath)}
                  sx={{ whiteSpace: "nowrap", minWidth: 90 }}
                >
                  Browse
                </Button>
              </Stack>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Connections (tasks per connection)
            </Typography>
            <Tooltip title="Add connection row">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="add connection row"
                  onClick={() => {
                    const n = nextUnusedChildPlanConnId;
                    if (n != null) setChildPlans([...childPlans, { connection_id: n, task_target_count: "1" }]);
                  }}
                  disabled={nextUnusedChildPlanConnId == null}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "primary.dark" },
                    "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {childPlans.map((row, i) => (
            <Box key={`${String(i)}-${String(row.connection_id)}`} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
              <TextField
                select
                label="Connection"
                value={String(row.connection_id)}
                sx={{ flex: 1 }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const next = childPlans.slice();
                  next[i] = { ...row, connection_id: Number(e.target.value) };
                  setChildPlans(next);
                }}
              >
                {connectionIdsForPlanRow(majorConnPickerIds, childPlans, i).map((cid) => {
                  const ordinal = (majorConnPickerIds.indexOf(cid) ?? -1) + 1;
                  return (
                    <MenuItem key={cid} value={String(cid)}>
                      {`${ordinal}. ${connectionPoolRowLabel(connections, cid) || "-"}`}
                    </MenuItem>
                  );
                })}
              </TextField>
              <TextField
                label="Tasks"
                inputProps={{ inputMode: "numeric" }}
                value={row.task_target_count}
                sx={{ width: 112 }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const next = childPlans.slice();
                  next[i] = {
                    ...row,
                    task_target_count: connPlanTasksDigitsOnly(e.target.value),
                  };
                  setChildPlans(next);
                }}
              />
              <Tooltip title="Remove row">
                <span>
                  <IconButton
                    size="small"
                    aria-label="remove connection row"
                    onClick={() => {
                      if (childPlans.length <= 1) return;
                      setChildPlans(childPlans.filter((_r, ri) => ri !== i));
                    }}
                    disabled={childPlans.length <= 1}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
          {childError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {childError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChildDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void createChildSubmit()} disabled={!canEditBots}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mjManageJobId != null}
        onClose={closeMjManage}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { ...DIALOG_MINOR_SX, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {mjEffectiveManagedJob ? mjJobDisplayName(mjEffectiveManagedJob) : "Manage job"}
              </Typography>
              {mjEffectiveManagedJob ? (
                <Typography variant="caption" color="text.secondary">
                  Task job{mjEffectiveManagedJobOrdinal != null ? ` #${mjEffectiveManagedJobOrdinal}` : ""}
                  {" · "}
                  {String(mjEffectiveManagedJob.bot_name ?? "-")}
                </Typography>
              ) : null}
            </Box>
            {mjEffectiveManagedJob ? (
              <StatusChipMj
                label={String(mjEffectiveManagedJob.task_status ?? "")}
                color={taskRunStatusChipColor(String(mjEffectiveManagedJob.task_status ?? ""))}
              />
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {activeMajor?.bot_kind === "upload_bots" && (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={mjManageTab}
                onChange={(_e: React.SyntheticEvent, v: number) => {
                  setMjManageTab(v);
                  if (v === 1 && mjManageJobId != null && matchesFiles === null) {
                    void loadMatchesFiles(mjManageJobId);
                  }
                }}
              >
                <Tab label="Overview" />
                <Tab label="Matches" />
              </Tabs>
            </Box>
          )}
          {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjManageJobError && (
            <Alert severity="error" sx={{ mb: 1.5, mt: 1 }}>
              {mjManageJobError}
            </Alert>
          )}
          {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjEffectiveManagedJob && (
            <Stack spacing={1.25} sx={{ mt: 1 }}>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Range ${String(mjEffectiveManagedJob.employee_number_from ?? 0)}-${String(
                    mjEffectiveManagedJob.employee_number_to ?? 0,
                  )}`}
                />
                {(() => {
                  const employeeStats = mjEmployeeStatsFromJob(mjEffectiveManagedJob);
                  return (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label={`Total ${employeeStats.total} · Success ${employeeStats.success} · Error ${employeeStats.error}`}
                    />
                  );
                })()}
                {(() => {
                  const stats = mjJobStatsTotals(mjEffectiveManagedJob);
                  return (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Tasks ${stats.running}/${stats.total}`}
                    />
                  );
                })()}
              </Stack>
              <Stack
                direction="row"
                gap={1}
                flexWrap="wrap"
                alignItems="center"
                sx={{ pt: 0.5 }}
              >
                {mjJobAggregateRunning(mjEffectiveManagedJob) ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={
                      isMjBatchStopping ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <StopIcon fontSize="small" />
                      )
                    }
                    onClick={() => void mjStopJob()}
                    disabled={mjJobActionBusy || isMjBatchStopping || !canEditBots}
                  >
                    {isMjBatchStopping ? "Stopping…" : "Stop all"}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={
                      isMjBatchRunning ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )
                    }
                    onClick={() => void mjRunJob()}
                    disabled={mjJobActionBusy || isMjBatchRunning || !canEditBots}
                  >
                    {isMjBatchRunning ? "Running…" : "Run all"}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    mjAddTasksLoading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Add fontSize="small" />
                    )
                  }
                  onClick={() => void openMjAddTasksDialog()}
                  disabled={mjJobActionBusy || mjAddTasksLoading || !canEditBots}
                >
                  {mjAddTasksLoading ? "Loading…" : "Add tasks"}
                </Button>
                <Button size="small" variant="outlined" onClick={openMjEditJobDialog} disabled={!canEditBots}>
                  Edit job
                </Button>
                {activeMajor?.bot_kind !== "delete_bots" && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => mjEffectiveManagedJob && handleMjViewJobFiles(mjEffectiveManagedJob)}
                  >
                    Files
                  </Button>
                )}
                <Tooltip title="Download all stored logs of this job as one .zip">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        isMjLogsArchiveLoading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <FileDownloadIcon fontSize="small" />
                        )
                      }
                      disabled={isMjLogsArchiveLoading || mjEffectiveManagedJob == null}
                      onClick={() =>
                        mjEffectiveManagedJob &&
                        void handleMjDownloadJobLogs(
                          Number(mjEffectiveManagedJob.id),
                          mjEffectiveManagedJob,
                        )
                      }
                    >
                      Download logs
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Delete all stored logs so the next run re-processes every employee">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<DeleteSweepIcon fontSize="small" />}
                      disabled={isMjClearLogsLoading || mjEffectiveManagedJob == null || !canEditBots}
                      onClick={() =>
                        mjEffectiveManagedJob &&
                        setMjClearLogsJobId(Number(mjEffectiveManagedJob.id))
                      }
                    >
                      Clear logs
                    </Button>
                  </span>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Delete task job">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Delete task job"
                      disabled={!canEditBots}
                      onClick={() => mjManageJobId != null && setMjDeleteJobId(mjManageJobId)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          )}
          {/* Matches tab — upload bots only */}
          {activeMajor?.bot_kind === "upload_bots" && mjManageTab === 1 && (
            <Box sx={{ pt: 1 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{
                  mb: 1.5,
                  py: 1,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                  Append reviewed rows to the Matches file
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<MergeTypeIcon fontSize="small" />}
                  disabled={matchesLoading || matchesDeleting || !canEditBots}
                  onClick={openMatchesMergeDialog}
                  sx={{ flexShrink: 0 }}
                >
                  Merge reports
                </Button>
              </Stack>
              {matchesMergeSuccess && (
                <Alert
                  severity="success"
                  sx={{ mb: 1 }}
                  onClose={() => setMatchesMergeSuccess(null)}
                >
                  {matchesMergeSuccess}
                </Alert>
              )}
              {matchesLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2, px: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading…</Typography>
                </Box>
              )}
              {matchesError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1 }}
                  action={
                    <Button
                      size="small"
                      onClick={() => mjManageJobId != null && void loadMatchesFiles(mjManageJobId)}
                    >
                      Retry
                    </Button>
                  }
                >
                  {matchesError}
                </Alert>
              )}
              {!matchesLoading && matchesFiles && (() => {
                const existingFiles = matchesFiles.filter((f) => f.exists);
                const allSelected =
                  existingFiles.length > 0 &&
                  matchesSelectedFiles.length === existingFiles.length;
                const someSelected =
                  matchesSelectedFiles.length > 0 && !allSelected;
                return (
                  <>
                    {matchesSelectedFiles.length > 0 && (
                      <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {matchesSelectedFiles.length} selected
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={matchesDeleting || !canEditBots}
                          startIcon={
                            matchesDeleting ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )
                          }
                          onClick={() =>
                            mjManageJobId != null &&
                            void confirmBatchDeleteMatchesFiles(mjManageJobId)
                          }
                        >
                          {matchesDeleting ? "Deleting…" : "Delete selected"}
                        </Button>
                        <Button
                          size="small"
                          disabled={matchesDeleting}
                          onClick={() => setMatchesSelectedFiles([])}
                        >
                          Clear
                        </Button>
                      </Stack>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 0, width: 44, pr: 0 }}>
                            <Checkbox
                              size="small"
                              indeterminate={someSelected}
                              checked={allSelected}
                              disabled={existingFiles.length === 0 || matchesDeleting}
                              onChange={(e) =>
                                setMatchesSelectedFiles(
                                  e.target.checked
                                    ? existingFiles.map((f) => f.filename)
                                    : [],
                                )
                              }
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, pl: 0 }}>File</TableCell>
                          <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Size</TableCell>
                          <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {matchesFiles.map((f) => {
                          const label =
                            f.filename === "s3_to_site_names_matches.csv"
                              ? "Matches"
                              : f.filename === "s3_to_site_names_several_matches.csv"
                                ? "Several matches"
                                : "No matches";
                          const sizeLabel =
                            f.size_bytes != null
                              ? f.size_bytes >= 1024
                                ? `${(f.size_bytes / 1024).toFixed(1)} KB`
                                : `${f.size_bytes} B`
                              : "—";
                          const isSelected = matchesSelectedFiles.includes(f.filename);
                          return (
                            <TableRow
                              key={f.filename}
                              selected={isSelected}
                              sx={{ opacity: matchesDeleting && isSelected ? 0.5 : 1 }}
                            >
                              <TableCell sx={{ pl: 0, pr: 0 }}>
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  disabled={!f.exists || matchesDeleting}
                                  onChange={(e) =>
                                    setMatchesSelectedFiles((prev) =>
                                      e.target.checked
                                        ? [...prev, f.filename]
                                        : prev.filter((n) => n !== f.filename),
                                    )
                                  }
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                              <TableCell sx={{ pl: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={f.exists ? 600 : 400}
                                  color={f.exists ? "text.primary" : "text.disabled"}
                                >
                                  {label}
                                </Typography>
                                {!f.exists && (
                                  <Typography variant="caption" color="text.disabled">
                                    not found
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                <Typography variant="caption" color="text.secondary">
                                  {f.exists ? sizeLabel : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ textAlign: "center", pr: 0 }}>
                                {f.exists ? (
                                  <Stack direction="row" gap={0.5} justifyContent="center">
                                    <Tooltip title="View">
                                      <IconButton
                                        size="small"
                                        disabled={matchesDeleting}
                                        onClick={() =>
                                          mjManageJobId != null &&
                                          void openMatchesFileViewer(mjManageJobId, f.filename)
                                        }
                                      >
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                      <IconButton
                                        size="small"
                                        disabled={matchesDeleting}
                                        onClick={() =>
                                          mjManageJobId != null &&
                                          void downloadMatchesFile(mjManageJobId, f.filename)
                                        }
                                      >
                                        <FileDownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={matchesDeleting || !canEditBots}
                                        onClick={() => setMatchesDeleteFilename(f.filename)}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                );
              })()}
              {!matchesLoading && !matchesError && matchesFiles === null && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  No data loaded.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeMjManage}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mjAddTasksOpen}
        onClose={() => setMjAddTasksOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>Add tasks</DialogTitle>
        <DialogContent>
          {mjAddTasksJobId != null && mjAddTasksDialogStats && (
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {mjAddTasksDialogStats.totalHelp}
              </Typography>
              {mjAddTasksDialogStats.planMode ? (
                <>
                  {mjBulkAddRows.map((row, ri) => {
                    const pickPool =
                      mjAddPlanConnPool.length > 0
                        ? mjAddPlanConnPool
                        : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id);
                    return (
                      <Box
                        key={`mj-add-${String(ri)}`}
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: ri === 0 ? 1 : 0.75,
                          mb: 0.25,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          select
                          label="Connection"
                          sx={{ flex: 1 }}
                          value={
                            typeof row.connection_id === "number" && row.connection_id >= 1
                              ? String(row.connection_id)
                              : ""
                          }
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const next = mjBulkAddRows.slice();
                            next[ri] = { ...row, connection_id: Number(e.target.value) };
                            setMjBulkAddRows(next);
                          }}
                        >
                          {connectionIdsForPlanRow(pickPool, mjBulkAddRows, ri).map((cid) => (
                            <MenuItem key={cid} value={String(cid)}>
                              {mjConnLabelVerbose(connections, cid)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          sx={{ width: 124 }}
                          label="Tasks"
                          inputProps={{ inputMode: "numeric" }}
                          value={row.task_target_count}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const next = mjBulkAddRows.slice();
                            next[ri] = {
                              ...row,
                              task_target_count: connPlanTasksDigitsOnly(e.target.value),
                            };
                            setMjBulkAddRows(next);
                          }}
                        />
                        <Tooltip title="Remove row">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="remove row"
                              onClick={() => {
                                if (mjBulkAddRows.length <= 1) return;
                                setMjBulkAddRows(mjBulkAddRows.filter((_r, ji) => ji !== ri));
                              }}
                              disabled={mjBulkAddRows.length <= 1}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    );
                  })}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
                    <Tooltip title="Add row for another login">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="add row"
                          onClick={() => {
                            const pool =
                              mjAddPlanConnPool.length > 0
                                ? mjAddPlanConnPool
                                : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id);
                            const n = nextUnusedPoolConnectionId(pool, mjBulkAddRows);
                            if (n != null) {
                              setMjBulkAddRows([...mjBulkAddRows, { connection_id: n, task_target_count: "1" }]);
                            }
                          }}
                          disabled={
                            nextUnusedPoolConnectionId(
                              mjAddPlanConnPool.length > 0
                                ? mjAddPlanConnPool
                                : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id),
                              mjBulkAddRows,
                            ) == null
                          }
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </>
              ) : null}
              {!mjAddTasksDialogStats.planMode && mjBulkAddRows[0] != null && (
                <TextField
                  sx={{ mt: 1 }}
                  fullWidth
                  label="Tasks to add"
                  inputProps={{ inputMode: "numeric" }}
                  value={mjBulkAddRows[0].task_target_count}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setMjBulkAddRows([
                      {
                        ...mjBulkAddRows[0],
                        task_target_count: connPlanTasksDigitsOnly(e.target.value),
                      },
                    ])
                  }
                />
              )}
            </Stack>
          )}
          {mjAddTasksDialogStats?.countHint?.trim() ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {mjAddTasksDialogStats.countHint}
            </Typography>
          ) : null}
          {!mjAddTasksErr && mjAddTasksDialogStats?.addBlockingReason?.trim() ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {mjAddTasksDialogStats.addBlockingReason}
            </Alert>
          ) : null}
          {mjAddTasksDialogStats?.planLevelHint?.trim() ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {mjAddTasksDialogStats.planLevelHint} The job&apos;s per-connection plan will be raised
              automatically when these tasks are added.
            </Alert>
          ) : null}
          {mjAddTasksErr ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {mjAddTasksErr}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMjAddTasksOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleMjAddTasksConfirmed()}
            disabled={
              isAddingTasksToJob ||
              mjAddTasksDialogStats == null ||
              Boolean(mjAddTasksDialogStats.addBlockingReason) ||
              !canEditBots
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mjEditJobOpen}
        onClose={() => setMjEditJobOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>
          Edit Task Job{mjEditJobOrdinal != null ? ` #${mjEditJobOrdinal}` : ""}
        </DialogTitle>
        <DialogContent>
          {mjEditJobHasRunningTasks ? (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              This task job has running tasks. Stop them before editing.
            </Alert>
          ) : null}
          {mjEditJobBotLocked ? (
            <Alert severity="info" sx={{ mt: mjEditJobHasRunningTasks ? 0 : 1, mb: 1 }}>
              This task job already has progress ({mjEditJobProcessedEmployees} employee
              {mjEditJobProcessedEmployees === 1 ? "" : "s"} processed). The bot cannot be changed.
            </Alert>
          ) : null}
          <TextField
            sx={{ mt: 1, mb: 1 }}
            fullWidth
            label="Task Job Name"
            required
            value={mjEditJobName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMjEditJobName(e.target.value)}
            disabled={mjEditJobHasRunningTasks}
          />
          <TextField
            sx={{ mb: 1 }}
            fullWidth
            label="Employees to"
            value={mjEditEmployeeTo}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            disabled={mjEditJobHasRunningTasks}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setMjEditEmployeeTo(sanitizeEmployeesInput(e.target.value))
            }
          />
          {mjEditJobBotLocked || mjEditJobHasRunningTasks ? (
            <TextField
              sx={{ mb: 1 }}
              fullWidth
              label="Bot"
              value={
                normalizedChildBots.find((b) => botNameFromRegistry(b) === mjEditBotName)?.bot_slug ??
                (mjEditBotName.includes(".") ? mjEditBotName.slice(mjEditBotName.indexOf(".") + 1) : mjEditBotName)
              }
              disabled
              helperText={
                mjEditJobBotLocked
                  ? "Cannot change bot while this task job has employee progress."
                  : ""
              }
            />
          ) : (
            <Autocomplete
              sx={{ mb: 1 }}
              options={normalizedChildBots}
              value={
                normalizedChildBots.find((b) => botNameFromRegistry(b) === mjEditBotName) ?? null
              }
              onChange={(_e, v) => setMjEditBotName(v ? botNameFromRegistry(v) : "")}
              filterOptions={filterChildDialogBots}
              getOptionLabel={(o) => o.bot_slug}
              renderOption={(props, option) => (
                <li {...props} key={`${option.vendor}.${option.job_kind}.${option.bot_slug}`}>
                  {option.bot_slug}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bot"
                  required
                  error={!mjEditBotName.trim() && Boolean(mjEditJobError)}
                />
              )}
            />
          )}
          <TextField
            sx={{ mb: 1 }}
            fullWidth
            multiline
            minRows={2}
            label="Additional options (applied to all tasks)"
            value={mjEditAdditionalOptions}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setMjEditAdditionalOptions(e.target.value)
            }
            disabled={mjEditJobHasRunningTasks}
          />
          {activeMajor?.bot_kind !== "delete_bots" && (
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Switch
                  checked={mjEditProcessErrors}
                  onChange={(e) => setMjEditProcessErrors(e.target.checked)}
                  disabled={mjEditJobHasRunningTasks}
                />
              }
              label="Process error employees"
            />
          )}
          {activeMajor?.bot_kind === "upload_bots" && (
            <TextField
              label="Document type *"
              size="small"
              fullWidth
              value={mjEditDocumentType}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMjEditDocumentType(e.target.value)}
              sx={{ mb: 1 }}
              placeholder="e.g. w2, paystubs, 1095"
              disabled={mjEditJobHasRunningTasks}
            />
          )}
          {activeMajor?.bot_kind === "delete_bots" && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Document types to delete *
              </Typography>
              {mjEditDocumentTypes.map((dt, i) => (
                <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. Paylocity Historical W2s"
                    value={dt}
                    disabled={mjEditJobHasRunningTasks}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setMjEditDocumentTypes((prev) => prev.map((v, j) => j === i ? e.target.value : v))
                    }
                  />
                  <IconButton size="small" color="error"
                    disabled={mjEditDocumentTypes.length === 1 || mjEditJobHasRunningTasks}
                    onClick={() => setMjEditDocumentTypes((prev) => prev.filter((_, j) => j !== i))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Add fontSize="small" />}
                disabled={mjEditJobHasRunningTasks}
                onClick={() => setMjEditDocumentTypes((prev) => [...prev, ""])}>
                Add type
              </Button>
            </Box>
          )}
          {activeMajor?.bot_kind === "upload_bots" && (
            <Box sx={{ mb: 1, mt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Upload options
              </Typography>
              <Stack direction="column" spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mjEditUploadAsZip}
                      onChange={(e) => setMjEditUploadAsZip(e.target.checked)}
                      size="small"
                      disabled={mjEditJobHasRunningTasks}
                    />
                  }
                  label="Upload as ZIP"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={mjEditUnzipBefore}
                      onChange={(e) => setMjEditUnzipBefore(e.target.checked)}
                      size="small"
                      disabled={mjEditJobHasRunningTasks}
                    />
                  }
                  label="Unzip before uploading"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={mjEditUseSsnMatch}
                      onChange={(e) => setMjEditUseSsnMatch(e.target.checked)}
                      size="small"
                      disabled={mjEditJobHasRunningTasks}
                    />
                  }
                  label="Use SSN match"
                />
              </Stack>
            </Box>
          )}
          {activeMajor?.bot_kind === "upload_bots" && (
            <Box sx={{ mb: 1.5, mt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Source folder (files to upload)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Select a folder from Data Storage…"
                  value={mjEditSourcePath}
                  InputProps={{ readOnly: true }}
                  disabled={mjEditJobHasRunningTasks}
                  sx={{ "& .MuiInputBase-input": { color: mjEditSourcePath ? "text.primary" : "text.disabled" } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FolderOpenIcon />}
                  disabled={mjEditJobHasRunningTasks}
                  onClick={() => openFolderPicker("edit", mjEditSourcePath)}
                  sx={{ whiteSpace: "nowrap", minWidth: 90 }}
                >
                  Browse
                </Button>
              </Stack>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Tasks and their connections are managed directly in the tasks table (Add tasks / row
            actions).
          </Typography>
          {mjEditJobError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {mjEditJobError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMjEditJobOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isMjEditJobSaving || mjEditJobHasRunningTasks || !canEditBots}
            onClick={() => void saveMjEditJob()}
          >
            {isMjEditJobSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteJobConfirmDialog
        open={mjDeleteJobId != null}
        title="Delete task job?"
        message={
          <>This permanently removes job{mjDeleteJobOrdinal != null ? ` #${mjDeleteJobOrdinal}` : ""}.</>
        }
        deleteData={mjDeleteJobLogs}
        onDeleteDataChange={setMjDeleteJobLogs}
        onCancel={() => {
          setMjDeleteJobId(null);
          setMjDeleteJobLogs(false);
        }}
        onConfirm={() => void mjConfirmDeleteJob()}
        confirmDisabled={!canEditBots}
        busy={isMjDeletingJob}
        paperSx={DIALOG_MINOR_SX}
      />

      <Dialog
        open={mjClearLogsJobId != null}
        onClose={() => setMjClearLogsJobId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>Clear all logs?</DialogTitle>
        <DialogContent sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            This permanently deletes all stored logs for this job. The next run will
            re-process every employee from scratch. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMjClearLogsJobId(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={isMjClearLogsLoading || !canEditBots}
            onClick={() => void handleMjClearJobLogs()}
          >
            Clear logs
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ssnJobDeleteConfirmOpen}
        onClose={() => setSsnJobDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>Delete SSN job?</DialogTitle>
        <DialogContent sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            This will permanently remove the SSN job and its task. The SSN file in S3 will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSsnJobDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={ssnJobDeleting || !canEditBots}
            onClick={async () => {
              if (!activeMajor || !canEditBots) return;
              setSsnJobDeleting(true);
              setSsnJobDeleteConfirmOpen(false);
              try {
                await deleteSsnJobMut(activeMajor.id).unwrap();
                await loadSsnJob(activeMajor.id);
              } catch {} finally { setSsnJobDeleting(false); }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={twoFaMjTask != null}
        onClose={() => {
          setTwoFaMjTask(null);
          setTwoFaMjCode("");
          setTwoFaMjErr(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>2FA code</DialogTitle>
        <DialogContent sx={{ mt: 0.5 }}>
          {twoFaMjTask ? (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Task{(() => {
                const jid = typeof twoFaMjTask.job === "number" ? twoFaMjTask.job : null;
                if (jid == null) return "";
                const ord = taskOrdinalInJob(jid, twoFaMjTask.id);
                return ord != null ? ` #${ord}` : "";
              })()}
            </Typography>
          ) : null}
          <TextField
            fullWidth
            autoFocus
            label="Code"
            value={twoFaMjCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setTwoFaMjCode(e.target.value)}
            onKeyDown={(ev) => {
              if (
                ev.key === "Enter" &&
                !ev.shiftKey &&
                twoFaMjCode.trim().length > 0 &&
                !isSubmit2FAMj &&
                canEditBots
              ) {
                ev.preventDefault();
                void handleMjSubmit2FACode();
              }
            }}
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
          />
          {twoFaMjErr ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {twoFaMjErr}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTwoFaMjTask(null);
              setTwoFaMjCode("");
              setTwoFaMjErr(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={isSubmit2FAMj || !canEditBots} onClick={() => void handleMjSubmit2FACode()}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mjDeleteSingleTaskId != null}
        onClose={() => setMjDeleteSingleTaskId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>Delete task?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Remove the selected task?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMjDeleteSingleTaskId(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={isDeletingNestedTask || !canEditBots} onClick={() => void mjConfirmDeleteNestedTask()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mjBatchDeleteNestDialog != null && (mjBatchDeleteNestDialog.ids?.length ?? 0) > 0}
        onClose={() => setMjBatchDeleteNestDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>Delete selected tasks?</DialogTitle>
        <DialogContent sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            Delete {mjBatchDeleteNestDialog?.ids.length ?? 0} task(s) from this job? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMjBatchDeleteNestDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isMjNestedTasksBatchDeleting || !canEditBots}
            onClick={() => void mjConfirmBatchDeleteNestedTasks()}
          >
            Delete all
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteJobConfirmDialog
        open={bulkChildDeleteOpen}
        title="Delete selected task jobs?"
        message={
          <>
            Delete {selectedChildJobIds.length} task job
            {selectedChildJobIds.length === 1 ? "" : "s"} and all their tasks for this major job? This cannot
            be undone.
          </>
        }
        deleteData={bulkChildDeleteLogs}
        onDeleteDataChange={setBulkChildDeleteLogs}
        onCancel={() => setBulkChildDeleteOpen(false)}
        onConfirm={() => void confirmBulkDeleteChildren()}
        confirmDisabled={!selectedChildJobIds.length || !canEditBots}
        busy={isDeletingJobsBulk}
        paperSx={DIALOG_MINOR_SX}
      />

      {/* ── Folder Picker Dialog (upload bots source folder) ─────────────────── */}
      <Dialog
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: DIALOG_MINOR_SX }}
      >
        <DialogTitle>Select source folder</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Choose the Data Storage folder containing the files you want to upload.
          </Typography>
          {storageFolders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No storage folders found for this user.
            </Typography>
          ) : (
            storageFolders.map((folder) => (
              <Box key={folder.id} sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 0.5,
                    cursor: "pointer",
                    color: pickerSelectedFolder === folder.id ? "primary.main" : "text.primary",
                  }}
                  onClick={() => {
                    setPickerSelectedFolder(folder.id);
                    setPickerSelectedSub(null);
                  }}
                >
                  {folder.display_name || folder.folder_prefix}
                </Typography>
                {folder.subfolders.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    No subfolders cached
                  </Typography>
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ ml: 1 }}>
                    {folder.subfolders.map((sub) => {
                      const isSelected = pickerSelectedFolder === folder.id && pickerSelectedSub === sub;
                      return (
                        <Box
                          key={sub}
                          onClick={() => {
                            setPickerSelectedFolder(folder.id);
                            setPickerSelectedSub(sub);
                          }}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            cursor: "pointer",
                            border: 1,
                            borderColor: isSelected ? "primary.main" : "divider",
                            bgcolor: isSelected ? "primary.50" : "background.paper",
                            color: isSelected ? "primary.main" : "text.primary",
                            fontSize: "0.8125rem",
                            fontWeight: isSelected ? 700 : 400,
                            "&:hover": { borderColor: "primary.main", bgcolor: "primary.50" },
                          }}
                        >
                          {sub}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            ))
          )}
          {pickerBuiltPath && (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, bgcolor: "action.hover" }}>
              <Typography variant="caption" color="text.secondary">
                Selected path:
              </Typography>
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.75rem", mt: 0.25 }}
              >
                {pickerBuiltPath}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderPickerOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pickerBuiltPath}
            onClick={confirmFolderPicker}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SSN File Viewer Dialog ────────────────────────────────────────────── */}
      <Dialog
        open={ssnFileViewOpen}
        onClose={() => setSsnFileViewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            ssn_info.json
          </Typography>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => activeMajor && void downloadSsnFile(activeMajor.id)}
              disabled={ssnFileViewLoading}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {ssnFileViewLoading ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
                overflow: "auto",
                maxHeight: "60vh",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {ssnFileViewContent ?? ""}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSsnFileViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Matches CSV Viewer Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={matchesViewOpen}
        onClose={() => setMatchesViewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {matchesViewFilename === "s3_to_site_names_matches.csv"
              ? "Matches"
              : matchesViewFilename === "s3_to_site_names_several_matches.csv"
                ? "Several matches"
                : "No matches"}
          </Typography>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => mjManageJobId != null && void downloadMatchesFile(mjManageJobId, matchesViewFilename)}
              disabled={matchesViewLoading}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        {matchesViewLoading && (
          <Box sx={{ px: 3 }}>
            <Box
              sx={{
                height: 3,
                bgcolor: "primary.main",
                borderRadius: 1,
                animation: "pulse 1.2s ease-in-out infinite",
                "@keyframes pulse": { "0%,100%": { opacity: 0.5 }, "50%": { opacity: 1 } },
              }}
            />
          </Box>
        )}
        <DialogContent dividers sx={{ p: 0, overflow: "auto", maxHeight: 520 }}>
          {matchesViewRows.length === 0 && !matchesViewLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
              (empty)
            </Typography>
          )}
          {matchesViewRows.length > 0 && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {matchesViewRows[0].map((col, ci) => (
                    <TableCell key={ci} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matchesViewRows.slice(1).map((row, ri) => (
                  <TableRow key={ri} hover>
                    {row.map((cell, ci) => (
                      <TableCell key={ci} sx={{ fontSize: "0.8rem", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
            {matchesViewRows.length > 1 ? `${matchesViewRows.length - 1} row${matchesViewRows.length - 1 === 1 ? "" : "s"}` : ""}
          </Typography>
          <Button onClick={() => setMatchesViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Matches Merge Reports Dialog ──────────────────────────────────────── */}
      <Dialog
        open={matchesMergeOpen}
        onClose={() => {
          if (matchesMergeStep !== "merging") resetMatchesMergeState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Merge reports</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a reviewed file (CSV, XLS, XLSX). Rows will be appended to the Matches
            file. Several matches and No matches files are not modified.
          </Typography>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              component="label"
              disabled={matchesMergeStep === "uploading" || matchesMergeStep === "merging" || !canEditBots}
            >
              Choose file
              <input
                type="file"
                hidden
                accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => void handleMatchesMergeFileSelect(e)}
              />
            </Button>
            {matchesMergeFile && (
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {matchesMergeFile.name}
              </Typography>
            )}
            {matchesMergeStep === "uploading" && (
              <Stack direction="row" gap={0.75} alignItems="center">
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Uploading…
                </Typography>
              </Stack>
            )}
            {matchesMergeStep === "ready" && (
              <Typography variant="caption" color="success.main">
                Ready to merge
              </Typography>
            )}
          </Stack>
          {matchesMergeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {matchesMergeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetMatchesMergeState}
            disabled={matchesMergeStep === "merging"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={matchesMergeStep !== "ready" || !matchesMergeStagingKey || !canEditBots}
            onClick={() => void confirmMatchesMerge()}
            startIcon={
              matchesMergeStep === "merging" ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <MergeTypeIcon fontSize="small" />
              )
            }
          >
            {matchesMergeStep === "merging" ? "Merging…" : "Merge"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Matches Delete Confirmation ───────────────────────────────────────── */}
      <Dialog
        open={matchesDeleteFilename != null}
        onClose={() => !matchesDeleting && setMatchesDeleteFilename(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete matches file?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{" "}
            <strong>
              {matchesDeleteFilename === "s3_to_site_names_matches.csv"
                ? "Matches"
                : matchesDeleteFilename === "s3_to_site_names_several_matches.csv"
                  ? "Several matches"
                  : "No matches"}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchesDeleteFilename(null)} disabled={matchesDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => mjManageJobId != null && void confirmDeleteMatchesFile(mjManageJobId)}
            disabled={matchesDeleting || !canEditBots}
            startIcon={matchesDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {matchesDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Logs Dialog (Status logs + File logs) ─────────────────────────────── */}
      <Dialog
        open={statusLogsOpen}
        onClose={() => setStatusLogsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <ArticleOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          Logs — {statusLogsJobName || `Job #${statusLogsJobId}`}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              color="primary"
              disabled={statusLogsRefreshing}
              onClick={() => {
                if (statusLogsJobId == null) return;
                if (logsTab === "file") void openFileLogs(statusLogsJobId, true);
                else void openStatusLogs(statusLogsJobId, undefined, true);
              }}
            >
              {statusLogsRefreshing ? (
                <CircularProgress size={18} color="primary" />
              ) : (
                <ReplayIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <Tabs
          value={logsTab}
          onChange={(_e, v: "status" | "file") => {
            setLogsTab(v);
            if (v === "file" && statusLogsJobId != null) void openFileLogs(statusLogsJobId);
          }}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider", minHeight: 40 }}
        >
          <Tab value="status" label="Status logs" sx={{ minHeight: 40 }} />
          <Tab value="file" label="File logs" sx={{ minHeight: 40 }} />
        </Tabs>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            ref={statusLogsPreRef}
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.78rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              m: 0,
              p: 2,
              maxHeight: 520,
              overflow: "auto",
              color: statusLogsRefreshing ? "text.disabled" : "text.primary",
              transition: "color 0.15s",
            }}
          >
            {(() => {
              const activeCache = logsTab === "file" ? fileLogsCache : statusLogsCache;
              if (statusLogsRefreshing) return "(loading…)";
              if (statusLogsJobId != null && activeCache[statusLogsJobId])
                return activeCache[statusLogsJobId].logs;
              return "(no data yet)";
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusLogsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Task error popup (clickable status chip) ──────────────────────────── */}
      <Dialog
        open={mjErrorPopupTask != null}
        onClose={() => setMjErrorPopupTask(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Task error</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1 }}>
            The bot ran into errors. Please rerun the task or contact the dev team.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMjErrorPopupTask(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={mjJobActionBusy || !canEditBots}
            onClick={() => {
              if (mjErrorPopupTask) {
                void mjRunTaskRow(mjErrorPopupTask.jobId, mjErrorPopupTask.task);
              }
              setMjErrorPopupTask(null);
            }}
          >
            Rerun
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
